import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Car, Gauge, Loader2, Plug, Plus, Satellite, Trash2, Wrench, Pencil, ShieldAlert, Clock, BookOpen, FileText, ExternalLink, Paperclip, Mail, Map as MapIcon, MapPin, Navigation, Power, WifiOff, Home, AlertTriangle, MessageCircle, Send, Play, Pause, Trophy, Route, Moon, RefreshCw } from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { CopilotDock } from "../../components/os/CopilotDock";
import { api, formatApiError } from "../../lib/api";

const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-violet transition-colors";
const fmtTz = (iso, tz) => {
  if (!iso) return "";
  const s = String(iso);
  try {
    const d = new Date(/[Zz]$|[+-]\d{2}:?\d{2}$/.test(s) ? s : s + "Z");
    return new Intl.DateTimeFormat("sv-SE", { timeZone: tz || "America/Mexico_City", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).format(d);
  } catch { return s.slice(0, 16).replace("T", " "); }
};
const fmtTzTime = (iso, tz) => (fmtTz(iso, tz) || "").slice(11);
const tzLabel = (tz) => tz ? `hora de ${String(tz).split("/").pop().replace(/_/g, " ")}` : "hora local";
const EMPTY = { nombre: "", placa: "", marca: "", modelo: "", anio: "", conductor: "", base: "", estado: "activa", odometro_km: 0, horario_inicio: "08:00", horario_fin: "18:00", limite_velocidad: null, strix_device_id: "", seguro_aseguradora: "", seguro_poliza: "", seguro_vence: "", verificacion_vence: "", tenencia_vence: "", licencia_vence: "", libre_deuda_fecha: "", libre_deuda_estado: "", notas: "" };

const DOC_TIPOS = [["poliza", "Póliza de seguro"], ["tarjeta_circulacion", "Tarjeta de circulación"], ["licencia_conductor", "Licencia del conductor"], ["otro", "Otro documento"]];

const vencBadges = (u) => {
  const today = new Date();
  return [["seguro_vence", "Seguro"], ["verificacion_vence", "Verif."], ["tenencia_vence", "Tenencia"], ["licencia_vence", "Licencia"]]
    .filter(([f]) => u[f])
    .map(([f, label]) => {
      const days = Math.floor((new Date(u[f]) - today) / 86400000);
      return { label, days, cls: days < 0 ? "bg-red-50 border-red-200 text-red-700" : days <= 30 ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-emerald-50 border-emerald-200 text-emerald-700" };
    });
};

export default function Fleet() {
  const [units, setUnits] = useState([]);
  const [sum, setSum] = useState(null);
  const [strix, setStrix] = useState(null);
  const tzName = strix?.timezone || "America/Mexico_City";
  const gLim = Number(strix?.limite_velocidad) || 65;
  const nocWin = `${strix?.nocturno_inicio || "20:00"}–${strix?.nocturno_fin || "06:00"}`;
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState({ base_url: "", username: "", password: "", account_id: "" });
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [geoVersion, setGeoVersion] = useState(0);
  const [bitacora, setBitacora] = useState(null);
  const [trailUnit, setTrailUnit] = useState(null);
  const [trailEpisode, setTrailEpisode] = useState(null);
  const [evFilter, setEvFilter] = useState("todos");
  const [unitQ, setUnitQ] = useState("");
  const unitsFiltered = unitQ.trim()
    ? units.filter((u) => [u.nombre, u.placa, u.marca, u.modelo, u.conductor].filter(Boolean).join(" ").toLowerCase().includes(unitQ.trim().toLowerCase()))
    : units;
  const [events, setEvents] = useState([]);
  const [evDate, setEvDate] = useState("");
  const [rescanning, setRescanning] = useState(false);
  const [egress, setEgress] = useState(null);
  const [loadingEgress, setLoadingEgress] = useState(false);

  const loadEvents = useCallback(() => {
    api.get("/fleet/events", { params: { limit: 500, ...(evDate ? { date: evDate } : {}) } }).then((r) => setEvents(r.data)).catch(() => {});
  }, [evDate]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const load = useCallback(() => {
    api.get("/fleet/units").then((r) => setUnits(r.data)).catch(() => {});
    api.get("/fleet/summary").then((r) => setSum(r.data)).catch(() => {});
    api.get("/fleet/strix-config").then((r) => { setStrix(r.data); setCfg((p) => ({ ...p, base_url: r.data.base_url, username: r.data.username, account_id: r.data.account_id })); }).catch(() => {});
    loadEvents();
  }, [loadEvents]);
  useEffect(() => { load(); }, [load]);

  // Pulso en vivo: refresca tablero, mapa y eventos cada 10s sin recargar la página
  useEffect(() => {
    const id = setInterval(() => {
      api.get("/fleet/summary").then((r) => setSum(r.data)).catch(() => {});
      api.get("/fleet/units").then((r) => setUnits(r.data)).catch(() => {});
      loadEvents();
    }, 10000);
    return () => clearInterval(id);
  }, [loadEvents]);

  const openEpisode = (ev, u) => {
    const base = new Date(ev.created_at).getTime();
    setTrailEpisode({
      from: ev.inicio || new Date(base - 5 * 60000).toISOString(),
      to: ev.fin || new Date(base + 5 * 60000).toISOString(),
      date: new Date(ev.inicio || ev.created_at).toLocaleDateString("en-CA", { timeZone: tzName }),
      velocidad: ev.velocidad, puntos: ev.puntos,
    });
    setTrailUnit(u);
  };

  const openJornadaMapa = (unitId, date) => {
    const u = units.find((x) => x.id === unitId);
    if (!u) { toast.error("Unidad no encontrada entre las activas"); return; }
    setTrailEpisode(date ? { date } : null);
    setTrailUnit(u);
  };

  const rescan = async () => {
    setRescanning(true);
    try {
      await api.post("/fleet/rescan-trails", null, { params: { days: 7 } });
      toast.success("Re-escaneo de los últimos 7 días iniciado — los eventos irán apareciendo aquí en unos minutos");
    } catch { toast.error("Error al iniciar el re-escaneo"); }
    finally { setTimeout(() => setRescanning(false), 8000); }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (modal.id) await api.patch(`/fleet/units/${modal.id}`, modal);
      else await api.post("/fleet/units", modal);
      toast.success(modal.id ? "Unidad actualizada" : "Unidad agregada");
      setModal(null); load();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail) || "Error"); }
    finally { setBusy(false); }
  };
  const del = async (u) => {
    if (!window.confirm(`¿Eliminar la unidad "${u.nombre}"? (recuperable desde la Papelera)`)) return;
    try { await api.delete(`/fleet/units/${u.id}`); toast.success("Unidad eliminada"); load(); } catch { toast.error("Error"); }
  };
  const saveStrix = async () => {
    try { await api.post("/fleet/strix-config", cfg); toast.success("Configuración Strix guardada"); setCfg((p) => ({ ...p, password: "" })); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  const testStrix = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/fleet/strix-test");
      (data.ok ? toast.success : toast.error)(data.detail || data.status);
      load();
    } catch { toast.error("Error al probar"); } finally { setTesting(false); }
  };
  const syncStrix = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/fleet/strix-sync");
      toast.success(data.detail);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo sincronizar"); }
    finally { setSyncing(false); }
  };
  const loadEgress = async () => {
    setLoadingEgress(true);
    try {
      const { data } = await api.get("/fleet/strix-egress-ip");
      setEgress(data);
      if (!data.egress_ip) toast.error("No se pudo determinar la IP de salida");
    } catch { toast.error("Error al consultar la IP de salida"); }
    finally { setLoadingEgress(false); }
  };
  const copyEgress = () => {
    if (egress?.egress_ip) { navigator.clipboard?.writeText(egress.egress_ip); toast.success("IP copiada"); }
  };
  const openBitacora = async (u) => {
    try { const { data } = await api.get(`/fleet/units/${u.id}/bitacora`); setBitacora(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };

  const K = ({ label, value, icon: Icon }) => (
    <div className="card-surface p-4 flex items-center gap-3">
      <Icon className="w-5 h-5 text-violet shrink-0" />
      <div><div className="text-xl font-semibold leading-none">{value}</div>
        <div className="text-[11px] text-navy/45 mt-1">{label}</div></div>
    </div>
  );

  const TONES = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    violet: "bg-violet-50 border-violet-200 text-violet-700",
    slate: "bg-navy/[0.03] border-navy/10 text-navy/50",
  };
  const LiveCard = ({ label, value, icon: Icon, tone = "slate", pulse, testid }) => (
    <div className={`rounded-xl border p-4 ${TONES[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 shrink-0" />
        {pulse && value > 0 && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
      </div>
      <div className="text-2xl font-semibold leading-none mt-2 text-navy">{value}</div>
      <div className="text-[11px] mt-1 opacity-80">{label}</div>
    </div>
  );

  return (
    <OSLayout title="Flotilla">
      <div className="space-y-8 p425" data-testid="fleet-page">
        {sum && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <K label="Unidades" value={sum.total} icon={Car} />
            <K label="Activas" value={sum.activas} icon={Gauge} />
            <K label="En taller" value={sum.taller} icon={Wrench} />
            <K label="Km registrados" value={sum.km_totales.toLocaleString()} icon={Satellite} />
          </div>
        )}

        {sum?.strix_conectado && sum?.vivo && (
          <div className="card-surface p-5" data-testid="fleet-live-dashboard">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <h3 className="font-medium">Pulso de la flotilla en vivo</h3>
              <span className="ml-auto text-[11px] text-navy/40 hidden sm:block" data-testid="fleet-live-caption">
                Strix · {sum.vivo.con_gps} con señal GPS de {sum.vivo.vinculadas} vinculadas
                {strix?.last_sync ? ` · act. ${fmtTzTime(strix.last_sync, tzName)} (${tzLabel(tzName)})` : ""}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <LiveCard icon={Navigation} tone="emerald" label="En movimiento" value={sum.vivo.en_movimiento} pulse testid="live-en-movimiento" />
              <LiveCard icon={Power} tone="amber" label="Encendidas detenidas" value={sum.vivo.encendidas} testid="live-encendidas" />
              <LiveCard icon={Satellite} tone="violet" label="Con señal GPS" value={sum.vivo.con_gps} testid="live-con-gps" />
              <LiveCard icon={WifiOff} tone="slate" label="Sin señal reciente" value={sum.vivo.sin_senal} testid="live-sin-senal" />
            </div>
            {sum.alertas_semana && (
              <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="fleet-live-alerts">
                <span className="inline-flex items-center gap-1.5 text-[11px] text-navy/50">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Alertas últimos 7 días:
                </span>
                <span className="text-[11px] rounded-full bg-navy text-cream px-3 py-1 font-medium">{sum.alertas_semana.total} total</span>
                <span className="text-[11px] rounded-full bg-red-50 border border-red-200 text-red-700 px-3 py-1">Exceso de velocidad · {sum.alertas_semana.exceso_velocidad}</span>
                <span className="text-[11px] rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 px-3 py-1">Mov. nocturno · {sum.alertas_semana.movimiento_nocturno}</span>
                <span className="text-[11px] rounded-full bg-amber-50 border border-amber-200 text-amber-700 px-3 py-1">Fuera de zona · {sum.alertas_semana.fuera_zona}</span>
              </div>
            )}
          </div>
        )}

        {/* Conexión Strix */}
        <div className="card-surface p-6" data-testid="fleet-strix-card">
          <div className="flex items-center gap-2 mb-1">
            <Satellite className="w-4 h-4 text-violet" />
            <h3 className="font-medium">Conexión Strix (rastreo GPS real)</h3>
            <span className={`ml-auto text-[11px] rounded-full px-3 py-1 border ${strix?.status === "conectado" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : strix?.status === "error" ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}
              data-testid="fleet-strix-status">
              {strix?.status === "conectado" ? `Conectado${strix?.vehiculos != null ? ` · ${strix.vehiculos} vehículo(s)` : ""}` : strix?.status === "error" ? "Error de conexión" : strix?.status === "credenciales_guardadas" ? "Credenciales guardadas" : "Pendiente de credenciales Strix"}
            </span>
          </div>
          <p className="text-xs text-navy/50 mb-4 max-w-2xl">
            Captura el usuario, la contraseña y el Account ID que te entregó Strix. Al conectar se sincroniza tu
            flotilla real cada 10 minutos: posición GPS, velocidad, encendido, km automáticos y alertas reales de
            exceso de velocidad y movimiento nocturno.
          </p>
          <div className="grid md:grid-cols-4 gap-2">
            <input className={inputCls} placeholder="Usuario Strix" value={cfg.username} data-testid="strix-username"
              onChange={(e) => setCfg({ ...cfg, username: e.target.value })} />
            <input className={inputCls} placeholder={strix?.has_password ? "Contraseña guardada ••••••••" : "Contraseña Strix"}
              type="password" value={cfg.password} data-testid="strix-password"
              onChange={(e) => setCfg({ ...cfg, password: e.target.value })} />
            <input className={inputCls} placeholder="Account ID (mrn:account:business:…)" value={cfg.account_id} data-testid="strix-account"
              onChange={(e) => setCfg({ ...cfg, account_id: e.target.value })} />
            <input className={inputCls} placeholder="URL base (api.flotas.strix.com.ar)" value={cfg.base_url} data-testid="strix-url"
              onChange={(e) => setCfg({ ...cfg, base_url: e.target.value })} />
            <input className={`${inputCls} md:col-span-2`} placeholder="Proxy propio opcional — OMNIA ya incluye un proxy argentino administrado, deja este campo vacío"
              value={cfg.proxy_url || ""} data-testid="strix-proxy"
              onChange={(e) => setCfg({ ...cfg, proxy_url: e.target.value })} />
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <button onClick={saveStrix} data-testid="strix-save" className="rounded-full bg-navy text-cream text-sm px-5 py-2.5 hover:bg-navy-soft transition-colors">Guardar</button>
            <button onClick={testStrix} disabled={testing} data-testid="strix-test"
              className="rounded-full border border-navy/15 text-sm px-5 py-2.5 hover:border-violet hover:text-violet transition-colors inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plug className="w-4 h-4" />} Probar conexión
            </button>
            <button onClick={syncStrix} disabled={syncing} data-testid="strix-sync"
              className="rounded-full bg-gradient-violet text-white text-sm px-5 py-2.5 hover:opacity-90 transition-colors inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Satellite className="w-4 h-4" />} Sincronizar ahora
            </button>
            {strix?.last_sync && <span className="text-[11px] text-navy/40 self-center" data-testid="strix-last-sync">Última sincronización: {fmtTz(strix.last_sync, tzName)} ({tzLabel(tzName)})</span>}
          </div>
          {strix?.last_error && <p className="text-xs text-red-600 mt-2" data-testid="strix-last-error">⚠ {strix.last_error}</p>}

          <div className="mt-4 rounded-xl border border-navy/10 bg-navy/[0.02] p-4" data-testid="strix-egress-panel">
            <div className="flex items-center gap-2 mb-1">
              <ShieldAlert className="w-3.5 h-3.5 text-violet" />
              <span className="overline text-navy/50">Salida hacia Strix (proxy administrado)</span>
            </div>
            <p className="text-xs text-navy/50 max-w-2xl mb-3">
              OMNIA incluye un proxy argentino administrado para pasar el WAF de Strix: no necesitas configurar
              nada ni pedir autorización de IPs. Aquí puedes consultar la IP de salida actual solo como referencia.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={loadEgress} disabled={loadingEgress} data-testid="strix-egress-btn"
                className="rounded-full border border-navy/15 text-sm px-4 py-2 hover:border-violet hover:text-violet transition-colors inline-flex items-center gap-1.5 disabled:opacity-50">
                {loadingEgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />} Consultar IP de salida
              </button>
              {egress?.egress_ip && (
                <div className="inline-flex items-center gap-2 rounded-full bg-white border border-navy/15 pl-4 pr-1.5 py-1" data-testid="strix-egress-ip">
                  <span className="font-mono text-sm text-navy">{egress.egress_ip}</span>
                  <span className="text-[10px] text-navy/40">{egress.via_proxy ? "(vía proxy)" : "(directa)"}</span>
                  <button onClick={copyEgress} data-testid="strix-egress-copy"
                    className="rounded-full bg-navy text-cream text-[11px] px-3 py-1 hover:bg-navy-soft transition-colors">Copiar</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Parámetros de alertas */}
        <AlertParamsCard strix={strix} onSaved={load} />

        {/* Roster CORRECTIVOS + Análisis de jornada */}
        <RosterCard onSynced={load} />
        <JornadaCard units={units} tzName={tzName} onVerMapa={openJornadaMapa} />

        {/* Panel de vencimientos documentales */}
        <VencimientosCard refreshKey={units.length} onOpenUnit={(uid) => { const u = units.find((x) => x.id === uid); if (u) openBitacora(u); }} />

        {/* Mapa en vivo */}
        <FleetMap units={units} geoVersion={geoVersion} tz={tzName} />

        {/* Geocercas */}
        <GeofencesCard units={units} onChange={() => setGeoVersion((v) => v + 1)} />

        {/* Reporte semanal */}
        <WeeklyReportCard tz={tzName} />

        {/* Alertas por WhatsApp */}
        <WhatsAppCard />

        {/* Ranking semanal de conductores */}
        <DriverRankingCard />

        {/* Unidades */}
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="overline text-navy/40">Unidades ({unitsFiltered.length}{unitQ ? ` de ${units.length}` : ""})</div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <input value={unitQ} onChange={(e) => setUnitQ(e.target.value)} placeholder="Buscar unidad, placa o conductor…"
                  className="bg-white border border-navy/10 rounded-full pl-4 pr-8 py-2 text-sm outline-none focus:border-violet w-64" data-testid="fleet-units-search" />
                {unitQ && <button onClick={() => setUnitQ("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-navy/35 hover:text-navy" data-testid="fleet-units-search-clear">✕</button>}
              </div>
              <button onClick={() => setModal({ ...EMPTY })} data-testid="fleet-new-unit"
                className="inline-flex items-center gap-2 rounded-full border border-navy/15 text-sm px-4 py-2 hover:border-violet hover:text-violet transition-colors">
                <Plus className="w-4 h-4" /> Agregar unidad
              </button>
            </div>
          </div>
          {units.length === 0 ? (
            <div className="text-sm text-navy/40 border border-dashed border-navy/15 rounded-lg py-8 text-center">
              Registra tus vehículos (nombre, placa, conductor). Cuando conectes Strix, cada unidad se vinculará por su ID de dispositivo.
            </div>
          ) : (
            <div className="overflow-x-auto border border-navy/10 rounded-lg bg-white">
              <table className="w-full text-sm">
                <thead className="bg-navy/[0.03] text-xs text-navy/50">
                  <tr>
                    {["Unidad", "Placa", "Marca / Modelo", "Conductor", "Odómetro (km)", "GPS Strix", "Vencimientos", "Estado", ""].map((h) => (
                      <th key={h} className="text-left px-3 py-2.5 font-medium whitespace-nowrap">{h}</th>))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy/5">
                  {unitsFiltered.map((u) => (
                    <tr key={u.id} data-testid={`fleet-unit-${u.id}`}>
                      <td className="px-3 py-2.5 font-medium whitespace-nowrap">{u.nombre}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{u.placa}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{u.marca} {u.modelo} {u.anio}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{u.conductor}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">{Number(u.odometro_km || 0).toLocaleString()}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap" data-testid={`fleet-gps-${u.id}`}>
                        {u.strix_live && u.strix_live.lat != null ? (
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <span className={`w-2 h-2 rounded-full ${isStale(u.strix_live) ? "bg-slate-300" : (Number(u.strix_live.speed) >= 7 || u.strix_live.contact_on) ? "bg-emerald-500 animate-pulse" : "bg-navy/20"}`} />
                            {isStale(u.strix_live) ? <span className="text-navy/40" title={`Última transmisión: ${fmtTz(u.strix_live.datetime, tzName)}`}>sin señal desde {fmtTzTime(u.strix_live.datetime, tzName)}</span> : (u.strix_live.speed != null ? `${u.strix_live.speed} km/h` : (u.strix_live.contact_on ? "encendida" : "apagada"))}
                            <a href={`https://maps.google.com/?q=${u.strix_live.lat},${u.strix_live.lng}`} target="_blank" rel="noreferrer"
                              className="text-violet hover:underline" title="Ver en Google Maps">mapa</a>
                          </span>
                        ) : u.strix_device_id ? <span className="text-xs text-navy/35">sin señal</span> : <span className="text-xs text-navy/25">—</span>}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        <div className="flex gap-1">
                          {vencBadges(u).map((b) => (
                            <span key={b.label} className={`text-[10px] rounded-full px-2 py-0.5 border ${b.cls}`} title={`${b.label}: ${b.days < 0 ? "vencido hace " + Math.abs(b.days) + "d" : "vence en " + b.days + "d"}`}>
                              {b.label} {b.days < 0 ? "⚠" : `${b.days}d`}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className={`text-[11px] rounded-full px-2.5 py-0.5 border ${u.estado === "activa" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : u.estado === "taller" ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-navy/5 border-navy/10 text-navy/50"}`}>{u.estado}</span>
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-right">
                        {u.strix_device_id && (
                          <button onClick={() => { setTrailEpisode(null); setTrailUnit(u); }} className="text-violet/70 hover:text-violet mr-2" title="Recorrido histórico" data-testid={`fleet-trail-${u.id}`}><Route className="w-4 h-4" /></button>
                        )}
                        <button onClick={() => openBitacora(u)} className="text-navy/40 hover:text-violet mr-2" title="Bitácora" data-testid={`fleet-bitacora-${u.id}`}><BookOpen className="w-4 h-4" /></button>
                        <button onClick={() => setModal({ ...u })} className="text-navy/40 hover:text-violet mr-2" title="Editar" data-testid={`fleet-edit-${u.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => del(u)} className="text-navy/30 hover:text-destructive" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Eventos telemáticos */}
        <div data-testid="fleet-events-section">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="overline text-navy/40">
              Eventos de alerta ({(evFilter === "todos" ? events : events.filter((e) => e.tipo === evFilter)).length}{evFilter !== "todos" ? ` de ${events.length}` : ""})
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-navy/50">Día:</span>
              <input type="date" max={new Date().toISOString().slice(0, 10)} value={evDate} data-testid="fleet-events-date"
                onChange={(e) => setEvDate(e.target.value)}
                className="bg-white border border-navy/10 rounded-lg px-2 py-1 text-xs outline-none focus:border-violet" />
              {evDate && (
                <button onClick={() => setEvDate("")} data-testid="fleet-events-date-clear"
                  className="rounded-full border border-navy/15 text-xs px-3 py-1 hover:border-violet hover:text-violet transition-colors">
                  Todos los días
                </button>
              )}
              <button onClick={rescan} disabled={rescanning} data-testid="fleet-events-rescan"
                className="rounded-full border border-navy/15 text-xs px-3 py-1 hover:border-violet hover:text-violet transition-colors disabled:opacity-40 inline-flex items-center gap-1.5">
                <RefreshCw className={`w-3 h-3 ${rescanning ? "animate-spin" : ""}`} /> Re-escanear 7 días
              </button>
              <span className="text-[11px] text-navy/40">100% datos reales del GPS de Strix</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="fleet-event-filters">
            {[["todos", "Todos", null],
              ["exceso_velocidad", `Excesos de velocidad > ${gLim} km/h`, ShieldAlert],
              ["movimiento_nocturno", `Movimiento nocturno ${nocWin}`, Moon],
              ["sin_senal", "Sin señal GPS", WifiOff],
              ["regreso_domicilio", "Regresos a domicilio", Home],
              ["fuera_zona_asignada", "Fuera de zona asignada", MapIcon],
              ["fuera_zona", "Fuera de zona", MapPin]].map(([key, label, Icon]) => {
              const n = key === "todos" ? events.length : events.filter((e) => e.tipo === key).length;
              return (
                <button key={key} onClick={() => setEvFilter(key)} data-testid={`fleet-events-filter-${key}`}
                  className={`inline-flex items-center gap-1.5 rounded-full text-xs px-3 py-1.5 border transition-colors ${evFilter === key ? "bg-navy text-cream border-navy" : "border-navy/15 text-navy/60 hover:border-violet hover:text-violet"}`}>
                  {Icon && <Icon className="w-3.5 h-3.5" />} {label} <span className={evFilter === key ? "opacity-70" : "text-navy/35"}>({n})</span>
                </button>
              );
            })}
          </div>
          {events.length === 0 ? (
            <div className="text-sm text-navy/40 border border-dashed border-navy/15 rounded-lg py-6 text-center" data-testid="fleet-events-empty">
              {evDate ? `Sin eventos registrados el ${evDate}. Prueba "Re-escanear 7 días" para buscar en el historial GPS.` : `Al conectar Strix, aquí llegarán automáticamente los excesos de velocidad y los movimientos nocturnos (${nocWin}), con aviso en la campana.`}
            </div>
          ) : (
            <div className="bg-white border border-navy/10 rounded-lg divide-y divide-navy/5 max-h-[440px] overflow-y-auto" data-testid="fleet-events-list">
              {(evFilter === "todos" ? events : events.filter((e) => e.tipo === evFilter)).map((ev) => {
                const evUnit = ev.tipo === "exceso_velocidad" ? units.find((u) => u.id === ev.unit_id && u.strix_device_id) : null;
                return (
                <div key={ev.id} onClick={evUnit ? () => openEpisode(ev, evUnit) : undefined}
                  className={`flex items-center gap-3 px-4 py-2.5 ${evUnit ? "cursor-pointer hover:bg-violet/5 transition-colors" : ""}`} data-testid={`fleet-event-${ev.id}`}>
                  {ev.tipo === "exceso_velocidad" ? <ShieldAlert className="w-4 h-4 text-red-500 shrink-0" /> : ev.tipo === "fuera_zona" ? <MapPin className="w-4 h-4 text-violet shrink-0" /> : ev.tipo === "movimiento_nocturno" ? <Moon className="w-4 h-4 text-indigo-500 shrink-0" /> : ev.tipo === "sin_senal" ? <WifiOff className="w-4 h-4 text-slate-400 shrink-0" /> : ev.tipo === "regreso_domicilio" ? <Home className="w-4 h-4 text-amber-600 shrink-0" /> : ev.tipo === "fuera_zona_asignada" ? <MapIcon className="w-4 h-4 text-sky-500 shrink-0" /> : <Clock className="w-4 h-4 text-amber-500 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">
                      <span className="font-medium">{ev.unidad}</span> — {ev.tipo === "exceso_velocidad" ? `${ev.velocidad} km/h máx (límite ${ev.limite ? Math.round(ev.limite) : gLim}${ev.zona_limite ? " de zona" : ""})${ev.puntos ? ` · ${ev.puntos} punto(s) GPS` : ""}${ev.inicio && ev.fin && ev.fin !== ev.inicio ? ` · ${fmtTzTime(ev.inicio, tzName)}–${fmtTzTime(ev.fin, tzName)}` : ""}` : ev.tipo === "fuera_zona" ? `salió de la zona "${ev.zona}"${ev.distancia_km != null ? ` (a ${ev.distancia_km} km)` : ""}` : ev.tipo === "movimiento_nocturno" ? `movimiento nocturno a las ${ev.hora}` : ev.tipo === "sin_senal" ? `sin señal GPS por ~${ev.horas} h (última señal ${ev.desde})` : ev.tipo === "regreso_domicilio" ? `regresó a la dirección del ${ev.rol} (${ev.quien}) de ${ev.hora} a ${ev.hasta}` : ev.tipo === "fuera_zona_asignada" ? `fuera de su zona ${ev.zona} de ${ev.desde} a ${ev.hasta}` : `movimiento a las ${ev.hora}`} · {ev.lugar}
                      {ev.simulado && <span className="text-[10px] text-navy/40 ml-1">[simulado]</span>}
                    </div>
                    <div className="text-[11px] text-navy/40">{fmtTz(ev.created_at, tzName)}</div>
                  </div>
                  {evUnit && (
                    <span className="text-[11px] text-violet shrink-0 inline-flex items-center gap-1" data-testid={`fleet-event-tramo-${ev.id}`}>
                      <MapIcon className="w-3 h-3" /> ver tramo
                    </span>
                  )}
                </div>
                );
              })}
              {(evFilter !== "todos" && events.filter((e) => e.tipo === evFilter).length === 0) && (
                <div className="text-sm text-navy/40 py-6 text-center" data-testid="fleet-events-empty-filter">Sin eventos de este tipo todavía.</div>
              )}
            </div>
          )}
        </div>

        {/* Métricas telemáticas */}
        {strix?.status !== "conectado" && (
        <div>
          <div className="overline text-navy/40 mb-3">Telemetría (se activa al conectar Strix)</div>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: Gauge, t: "Km por unidad", d: "Distancia recorrida por día/semana/mes con histórico." },
              { icon: ShieldAlert, t: "Excesos de velocidad", d: `Unidades que superen el límite configurado (global ${gLim} km/h o el propio de la unidad) con fecha, lugar y conductor.` },
              { icon: Moon, t: "Movimiento nocturno", d: `Unidades con movimiento entre ${nocWin} hora local.` },
            ].map((m) => (
              <div key={m.t} className="rounded-xl border border-dashed border-navy/15 p-5 opacity-70">
                <m.icon className="w-5 h-5 text-navy/40 mb-2" />
                <div className="text-sm font-medium">{m.t}</div>
                <div className="text-xs text-navy/45 mt-1">{m.d}</div>
              </div>
            ))}
          </div>
        </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[70] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fleet-unit-modal">
          <form onSubmit={save} className="bg-cream rounded-lg p-6 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{modal.id ? "Editar unidad" : "Agregar unidad"}</h3>
              <button type="button" onClick={() => setModal(null)} className="text-navy/40 hover:text-navy">✕</button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input className={inputCls} placeholder="Nombre / número de unidad *" required value={modal.nombre} data-testid="unit-nombre"
                onChange={(e) => setModal({ ...modal, nombre: e.target.value })} />
              <input className={inputCls} placeholder="Placa" value={modal.placa} onChange={(e) => setModal({ ...modal, placa: e.target.value })} />
              <input className={inputCls} placeholder="Marca" value={modal.marca} onChange={(e) => setModal({ ...modal, marca: e.target.value })} />
              <input className={inputCls} placeholder="Modelo" value={modal.modelo} onChange={(e) => setModal({ ...modal, modelo: e.target.value })} />
              <input className={inputCls} placeholder="Año" value={modal.anio} onChange={(e) => setModal({ ...modal, anio: e.target.value })} />
              <input className={inputCls} placeholder="Conductor asignado" value={modal.conductor} onChange={(e) => setModal({ ...modal, conductor: e.target.value })} />
              <input className={inputCls} placeholder="Base / sucursal" value={modal.base} onChange={(e) => setModal({ ...modal, base: e.target.value })} />
              <input className={inputCls} placeholder="Odómetro (km)" type="number" value={modal.odometro_km}
                onChange={(e) => setModal({ ...modal, odometro_km: Number(e.target.value) })} />
              <select className={inputCls} value={modal.estado} onChange={(e) => setModal({ ...modal, estado: e.target.value })}>
                <option value="activa">Activa</option><option value="taller">En taller</option><option value="baja">Baja</option>
              </select>
              <input className={inputCls} placeholder="ID dispositivo Strix (opcional)" value={modal.strix_device_id}
                onChange={(e) => setModal({ ...modal, strix_device_id: e.target.value })} />
              <div className="col-span-2 grid grid-cols-3 gap-2">
                <input className={`${inputCls} col-span-2`} type="number" title="Límite de velocidad propio de la unidad (vacío = usa el límite global)" placeholder="Límite km/h propio (vacío = global)"
                  value={modal.limite_velocidad ?? ""} onChange={(e) => setModal({ ...modal, limite_velocidad: e.target.value === "" ? null : Number(e.target.value) })} />
              </div>
              <div className="col-span-2 grid grid-cols-2 gap-2">
                <input className={inputCls} placeholder="Aseguradora" value={modal.seguro_aseguradora || ""} data-testid="unit-aseguradora"
                  onChange={(e) => setModal({ ...modal, seguro_aseguradora: e.target.value })} />
                <input className={inputCls} placeholder="No. de póliza" value={modal.seguro_poliza || ""}
                  onChange={(e) => setModal({ ...modal, seguro_poliza: e.target.value })} />
                <label className="text-[10px] text-navy/40">Seguro vence
                  <input className={inputCls} type="date" value={modal.seguro_vence || ""} data-testid="unit-seguro-vence"
                    onChange={(e) => setModal({ ...modal, seguro_vence: e.target.value })} /></label>
              </div>
              <div className="col-span-2 grid grid-cols-3 gap-2">
                <label className="text-[10px] text-navy/40">Verificación vence
                  <input className={inputCls} type="date" value={modal.verificacion_vence || ""}
                    onChange={(e) => setModal({ ...modal, verificacion_vence: e.target.value })} /></label>
                <label className="text-[10px] text-navy/40">Tenencia vence
                  <input className={inputCls} type="date" value={modal.tenencia_vence || ""}
                    onChange={(e) => setModal({ ...modal, tenencia_vence: e.target.value })} /></label>
                <label className="text-[10px] text-navy/40">Licencia vence
                  <input className={inputCls} type="date" value={modal.licencia_vence || ""} data-testid="unit-licencia-vence"
                    onChange={(e) => setModal({ ...modal, licencia_vence: e.target.value })} /></label>
              </div>
              <div className="col-span-2 grid grid-cols-2 gap-2">
                <label className="text-[10px] text-navy/40">Libre deuda chequeado el
                  <input className={inputCls} type="date" value={modal.libre_deuda_fecha || ""} data-testid="unit-libre-deuda-fecha"
                    onChange={(e) => setModal({ ...modal, libre_deuda_fecha: e.target.value })} /></label>
                <label className="text-[10px] text-navy/40">Estado libre deuda
                  <select className={inputCls} value={modal.libre_deuda_estado || ""} data-testid="unit-libre-deuda-estado"
                    onChange={(e) => setModal({ ...modal, libre_deuda_estado: e.target.value })}>
                    <option value="">—</option>
                    <option value="libre">Libre de deuda ✓</option>
                    <option value="con_deuda">Con deuda</option>
                  </select></label>
              </div>
              <textarea className={`${inputCls} col-span-2`} rows={2} placeholder="Notas" value={modal.notas}
                onChange={(e) => setModal({ ...modal, notas: e.target.value })} />
            </div>
            <button disabled={busy} data-testid="unit-submit"
              className="w-full rounded-full bg-gradient-violet text-white py-3 hover:opacity-90 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Car className="w-4 h-4" />} {modal.id ? "Guardar cambios" : "Agregar unidad"}
            </button>
          </form>
        </div>
      )}
      {trailUnit && (
        <div className="fixed inset-0 z-[75] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fleet-trail-modal">
          <div className="bg-cream rounded-lg w-full max-w-2xl max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-navy/10">
              <div className="font-medium flex items-center gap-2">
                <Route className="w-4 h-4 text-violet" /> Recorrido · {trailUnit.nombre}
                <span className="text-navy/40 text-sm font-normal">{trailUnit.placa}</span>
              </div>
              <button onClick={() => { setTrailUnit(null); setTrailEpisode(null); }} className="text-navy/40 hover:text-navy" data-testid="fleet-trail-close">✕</button>
            </div>
            <div className="overflow-y-auto p-5">
              <TrailExplorer unit={trailUnit} autoLoad tz={tzName} episode={trailEpisode} globalLimit={gLim} />
            </div>
          </div>
        </div>
      )}

      {bitacora && (
        <div className="fixed inset-0 z-[75] bg-navy/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="fleet-bitacora-modal">
          <div className="bg-cream rounded-lg w-full max-w-2xl max-h-[88vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-navy/10">
              <div className="font-medium flex items-center gap-2"><BookOpen className="w-4 h-4 text-violet" /> Bitácora · {bitacora.unidad.nombre} <span className="text-navy/40 text-sm font-normal">{bitacora.unidad.placa}</span></div>
              <button onClick={() => setBitacora(null)} className="text-navy/40 hover:text-navy" data-testid="fleet-bitacora-close">✕</button>
            </div>
            <div className="overflow-y-auto p-5 space-y-5">
              <div>
                <div className="overline text-navy/40 mb-2">Vencimientos</div>
                {bitacora.vencimientos.length === 0 ? (
                  <p className="text-sm text-navy/40">Sin fechas registradas. Edita la unidad para capturar seguro, verificación, tenencia y licencia — recibirás recordatorios 30/14/7/1 días antes.</p>
                ) : (
                  <div className="space-y-1.5">
                    {bitacora.vencimientos.map((v) => (
                      <div key={v.tipo} className="flex items-center gap-2 text-sm">
                        <span className={`w-2 h-2 rounded-full ${v.estado === "vencido" ? "bg-red-500" : v.estado === "por_vencer" ? "bg-amber-500" : "bg-emerald-500"}`} />
                        <span className="font-medium">{v.tipo}</span>
                        <span className="text-navy/50">{v.vence} · {v.dias < 0 ? `vencido hace ${Math.abs(v.dias)} días` : `en ${v.dias} días`}</span>
                        {v.extra && <span className="text-navy/40 text-xs">{v.extra}</span>}
                        {v.doc_tipo && v.doc_adjunto === false && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700" data-testid={`venc-sin-doc-${v.doc_tipo}`}>⚠️ sin PDF adjunto</span>
                        )}
                        {v.doc_tipo && v.doc_adjunto === true && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">PDF adjunto</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {bitacora.unidad.libre_deuda_fecha && (
                  <div className="flex items-center gap-2 text-sm mt-1.5" data-testid="bitacora-libre-deuda">
                    <span className={`w-2 h-2 rounded-full ${bitacora.unidad.libre_deuda_estado === "con_deuda" ? "bg-red-500" : "bg-emerald-500"}`} />
                    <span className="font-medium">Libre deuda</span>
                    <span className="text-navy/50">chequeado el {bitacora.unidad.libre_deuda_fecha}</span>
                    {bitacora.unidad.libre_deuda_estado === "con_deuda"
                      ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700 font-medium">CON DEUDA</span>
                      : <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">libre de deuda ✓</span>}
                  </div>
                )}
              </div>
              <div>
                <div className="overline text-navy/40 mb-2">Documentos (PDF privados)</div>
                <UnitDocs unitId={bitacora.unidad.id} />
              </div>
              {bitacora.renovaciones?.length > 0 && (
                <div data-testid="bitacora-renovaciones">
                  <div className="overline text-navy/40 mb-2">Historial documental (renovaciones)</div>
                  <div className="relative pl-5">
                    <span className="absolute left-[5px] top-1.5 bottom-1.5 w-px bg-navy/10" />
                    <div className="space-y-3">
                      {bitacora.renovaciones.map((r) => (
                        <div key={r.doc_id} className="relative text-sm" data-testid="renovacion-item">
                          <span className={`absolute -left-5 top-1 w-2.5 h-2.5 rounded-full border-2 border-white ${r.deuda === "con_deuda" ? "bg-red-500 ring-2 ring-red-200" : r.vigente ? "bg-emerald-500 ring-2 ring-emerald-200" : "bg-navy/25"}`} />
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">
                              {r.tipo === "poliza"
                                ? `${r.aseguradora || "Póliza"}${r.nro_poliza ? ` · ${r.nro_poliza}` : ""}`
                                : `${r.tipo_label}${r.titular ? ` · ${r.titular}` : ""}`}
                            </span>
                            {r.tipo === "poliza" && <span className="text-[11px] text-navy/35 uppercase tracking-wide">Póliza</span>}
                            {r.vigencia_hasta && <span className="text-navy/50">vigente hasta {r.vigencia_hasta}</span>}
                            {!r.vigencia_hasta && r.fecha && <span className="text-navy/50">del {r.fecha}</span>}
                            {r.deuda === "con_deuda" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700 font-medium">CON DEUDA</span>}
                            {r.vigente && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium" data-testid="renovacion-vigente">VIGENTE</span>}
                            {r.url && (
                              <a href={r.url} target="_blank" rel="noreferrer" className="text-navy/35 hover:text-violet transition-colors" title={`Abrir PDF (${r.filename})`} data-testid={`renovacion-pdf-${r.doc_id}`}>
                                <ExternalLink className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                          <div className="text-xs text-navy/40 mt-0.5">
                            subida por {r.subido_por || "—"} el {r.subido_el}{r.fuente === "docshub" ? " · vía Documents Hub" : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {bitacora.unidad.strix_device_id && <StrixBlock unit={bitacora.unidad} tz={tzName} globalLimit={gLim} />}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="overline text-navy/40">Servicios en el taller (Workshop Studio)</div>
                  <a href="/os/mod/ws_work_orders" className="text-xs text-violet hover:underline">Abrir Workshop →</a>
                </div>
                {bitacora.ordenes_taller.length === 0 ? (
                  <p className="text-sm text-navy/40">Sin órdenes de trabajo ligadas a esta placa/unidad. Las órdenes creadas en Workshop con la placa "{bitacora.unidad.placa || bitacora.unidad.nombre}" aparecerán aquí automáticamente.</p>
                ) : (
                  <div className="bg-white border border-navy/10 rounded-lg divide-y divide-navy/5">
                    {bitacora.ordenes_taller.map((o) => (
                      <div key={o.id} className="px-3 py-2.5 text-sm flex items-center gap-3">
                        <Wrench className="w-4 h-4 text-navy/30 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">{o.folio} · {o.tipo}</div>
                          <div className="text-[11px] text-navy/40 truncate">{o.falla} · {o.mecanico} {o.costo_total ? `· $${Number(o.costo_total).toLocaleString()}` : ""}</div>
                        </div>
                        <span className="text-[10px] rounded-full px-2 py-0.5 border border-navy/10 text-navy/50">{o.estado}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {bitacora.expediente_taller && (
                <p className="text-xs text-navy/45">
                  Expediente en taller: unidad {bitacora.expediente_taller.economico} · {Number(bitacora.expediente_taller.kilometraje || 0).toLocaleString()} km ·
                  último mant. {bitacora.expediente_taller.mant_ultima_fecha || "—"}
                </p>
              )}
              {bitacora.eventos.length > 0 && (
                <div>
                  <div className="overline text-navy/40 mb-2">Eventos de alerta</div>
                  {bitacora.eventos.slice(0, 8).map((ev) => (
                    <div key={ev.id} className="text-xs text-navy/60 py-1">
                      {ev.tipo === "exceso_velocidad" ? "🚨" : ev.tipo === "fuera_zona" ? "📍" : "🌙"} {fmtTz(ev.created_at, tzName)} — {ev.tipo === "exceso_velocidad" ? `${ev.velocidad} km/h` : ev.tipo === "fuera_zona" ? `fuera de zona "${ev.zona}"` : `movimiento ${ev.hora}`} · {ev.lugar}{ev.simulado ? " [simulado]" : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <CopilotDock title="Copiloto · Flotilla"
        suggestions={["¿Qué unidades tienen documentos faltantes?", "¿Qué vencimientos hay en los próximos 30 días?", "Muéstrame los últimos eventos de velocidad y horario"]} />
    </OSLayout>
  );
}

const mapDot = (color, pulse) => L.divIcon({
  className: "", iconSize: [18, 18], iconAnchor: [9, 9],
  html: `<span style="display:block;width:16px;height:16px;border-radius:9999px;background:${color};border:2.5px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,.4)${pulse ? ";animation:pulse 1.6s infinite" : ""}"></span>`,
});

const homePin = (color) => L.divIcon({
  className: "", iconSize: [26, 26], iconAnchor: [13, 24],
  html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center"><svg style="transform:rotate(45deg)" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V21h14V9.5"/></svg></div>`,
});
const HOME_COLORS = { chofer: "#0D9488", ayudante: "#2563EB", tecnico: "#D97706", base: "#0E1A2B" };
const HOME_LABELS = { chofer: "Chofer", ayudante: "Ayudante", tecnico: "Técnico", base: "Base" };

const havKm = (lat1, lng1, lat2, lng2) => {
  const rad = (x) => (x * Math.PI) / 180, R = 6371;
  const h = Math.sin(rad(lat2 - lat1) / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lng2 - lng1) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};
const zoneLimit = (p, unitId, fences) => {
  if (p?.lat == null || !fences?.length) return null;
  const lims = fences
    .filter((g) => g.limite_velocidad && g.lat != null && (!(g.unit_ids || []).length || g.unit_ids.includes(unitId))
      && havKm(p.lat, p.lng, g.lat, g.lng) * 1000 <= (Number(g.radio_m) || 1000))
    .map((g) => Number(g.limite_velocidad));
  return lims.length ? Math.min(...lims) : null;
};

const STALE_MIN = 20;
// Fix viejo que además dice estar en movimiento/encendida = GPS congelado.
const isStale = (lv) => !!lv?.datetime && (Date.now() - new Date(lv.datetime).getTime()) > STALE_MIN * 60000
  && (Number(lv.speed) >= 7 || lv.contact_on === true || lv.in_motion === true);

const claseLive = (u) => {
  const lv = u.strix_live || {};
  if (isStale(lv)) return "sin_senal";
  return (Number(lv.speed) >= 7 || lv.in_motion === true) ? "movimiento" : lv.contact_on ? "encendida" : "apagada";
};

function FleetMap({ units, geoVersion, tz }) {
  const [liveUnits, setLiveUnits] = useState(units);
  const [fences, setFences] = useState([]);
  const [mapFilter, setMapFilter] = useState("todos");
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layerRef = useRef(null);
  const fittedRef = useRef(null);

  useEffect(() => setLiveUnits(units), [units]);
  useEffect(() => {
    api.get("/fleet/geofences").then((r) => setFences(r.data.filter((f) => f.activo))).catch(() => {});
  }, [geoVersion]);

  const geo = liveUnits.filter((u) => u.strix_live && u.strix_live.lat != null && u.estado !== "baja");
  const shown = mapFilter === "todos" ? geo : geo.filter((u) => claseLive(u) === mapFilter);

  useEffect(() => {
    if (!mapRef.current || mapObj.current || geo.length === 0) return;
    const m = L.map(mapRef.current, { scrollWheelZoom: true }).setView([geo[0].strix_live.lat, geo[0].strix_live.lng], 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 }).addTo(m);
    mapObj.current = m;
    setTimeout(() => m.invalidateSize(), 200);
    return () => { m.remove(); mapObj.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo.length > 0]);

  useEffect(() => {
    const m = mapObj.current;
    if (!m) return;
    if (layerRef.current) layerRef.current.remove();
    const g = L.layerGroup();
    const bounds = [];
    fences.forEach((f) => {
      L.circle([f.lat, f.lng], { radius: f.radio_m, color: "#6C5CE7", weight: 1.5, fillColor: "#6C5CE7", fillOpacity: 0.08 })
        .bindPopup(`<b>Zona: ${f.nombre}</b><br/>Radio: ${f.radio_m >= 1000 ? (f.radio_m / 1000).toFixed(1) + " km" : f.radio_m + " m"}`)
        .addTo(g);
    });
    shown.forEach((u) => {
      const lv = u.strix_live;
      const stale = isStale(lv);
      const moving = !stale && (Number(lv.speed) >= 7 || lv.in_motion === true);
      const color = stale ? "#94a3b8" : moving ? "#10B981" : lv.contact_on ? "#f59e0b" : "#94a3b8";
      const estado = stale ? `Sin señal desde ${fmtTz(lv.datetime, tz)}` : moving ? `En movimiento · ${lv.speed} km/h` : lv.contact_on ? "Encendida (detenida)" : "Apagada";
      L.marker([lv.lat, lv.lng], { icon: mapDot(color, moving) })
        .bindPopup(`<b>${u.nombre}</b>${u.placa ? " · " + u.placa : ""}<br/>` +
          `<span style="color:${color}">●</span> ${estado}<br/>` +
          (u.conductor ? `Conductor: ${u.conductor}<br/>` : "") +
          `Odómetro: ${Number(u.odometro_km || 0).toLocaleString()} km<br/>` +
          (lv.updated_at ? `<span style="color:#888;font-size:11px">Actualizado ${fmtTz(lv.updated_at, tz)} (${tzLabel(tz)})</span><br/>` : "") +
          `<a href="https://maps.google.com/?q=${lv.lat},${lv.lng}" target="_blank" rel="noreferrer">Abrir en Google Maps</a>`)
        .addTo(g);
      bounds.push([lv.lat, lv.lng]);
    });
    g.addTo(m);
    layerRef.current = g;
    if (bounds.length && fittedRef.current !== mapFilter) { m.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 }); fittedRef.current = mapFilter; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveUnits, fences, tz, mapFilter]);

  if (geo.length === 0) return null;
  const nDe = (k) => geo.filter((u) => claseLive(u) === k).length;
  return (
    <div className="card-surface p-6" data-testid="fleet-map-card">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <MapIcon className="w-4 h-4 text-violet" />
        <h3 className="font-medium">Mapa de la flotilla en vivo</h3>
        <span className="text-xs text-navy/40">· {shown.length} de {geo.length} unidad(es) · se actualiza cada 10 segundos</span>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {[["todos", "Todos", "#0A1428", geo.length],
            ["movimiento", "En movimiento", "#10B981", nDe("movimiento")],
            ["encendida", "Encendida", "#f59e0b", nDe("encendida")],
            ["apagada", "Apagada", "#94a3b8", nDe("apagada")],
            ["sin_senal", "Sin señal reciente", "#cbd5e1", nDe("sin_senal")]].map(([key, label, color, n]) => (
            <button key={key} onClick={() => setMapFilter(key)} data-testid={`fleet-map-filter-${key}`}
              className={`inline-flex items-center gap-1.5 rounded-full text-[11px] px-2.5 py-1 border transition-colors ${mapFilter === key ? "bg-navy text-cream border-navy" : "border-navy/15 text-navy/55 hover:border-violet hover:text-violet"}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: color }} /> {label} ({n})
            </button>
          ))}
        </div>
      </div>
      <div ref={mapRef} style={{ height: 420 }} className="rounded-xl border border-navy/10 overflow-hidden" data-testid="fleet-map-canvas" />
    </div>
  );
}

function GeofencesCard({ units, onChange }) {
  const [fences, setFences] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", lat: null, lng: null, radio_m: 1000, unit_ids: [], limite_velocidad: "" });
  const [saving, setSaving] = useState(false);
  const miniRef = useRef(null);
  const miniObj = useRef(null);
  const shapeRef = useRef(null);

  const load = useCallback(() => api.get("/fleet/geofences").then((r) => setFences(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!formOpen || !miniRef.current || miniObj.current) return;
    const withGps = units.find((u) => u.strix_live?.lat != null);
    const center = withGps ? [withGps.strix_live.lat, withGps.strix_live.lng] : [19.4326, -99.1332];
    const m = L.map(miniRef.current, { scrollWheelZoom: true }).setView(center, 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 }).addTo(m);
    m.on("click", (e) => setForm((f) => ({ ...f, lat: +e.latlng.lat.toFixed(6), lng: +e.latlng.lng.toFixed(6) })));
    miniObj.current = m;
    setTimeout(() => m.invalidateSize(), 200);
    return () => { m.remove(); miniObj.current = null; shapeRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formOpen]);

  useEffect(() => {
    const m = miniObj.current;
    if (!m) return;
    if (shapeRef.current) shapeRef.current.remove();
    if (form.lat == null) return;
    shapeRef.current = L.circle([form.lat, form.lng], { radius: Number(form.radio_m) || 100, color: "#6C5CE7", fillColor: "#6C5CE7", fillOpacity: 0.15 }).addTo(m);
  }, [form.lat, form.lng, form.radio_m]);

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("Ponle nombre a la zona (ej. CEDIS Norte)");
    if (form.lat == null) return toast.error("Haz clic en el mapa para fijar el centro de la zona");
    setSaving(true);
    try {
      await api.post("/fleet/geofences", { ...form, radio_m: Number(form.radio_m) || 1000, limite_velocidad: form.limite_velocidad === "" ? null : Number(form.limite_velocidad), activo: true });
      toast.success(`Zona "${form.nombre}" creada — se alertará cuando una unidad salga de ella`);
      setForm({ nombre: "", lat: null, lng: null, radio_m: 1000, unit_ids: [], limite_velocidad: "" });
      setFormOpen(false);
      load(); onChange?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
    finally { setSaving(false); }
  };
  const toggleActivo = async (f) => {
    try { await api.put(`/fleet/geofences/${f.id}`, { ...f, activo: !f.activo }); load(); onChange?.(); }
    catch { toast.error("Error"); }
  };
  const del = async (f) => {
    if (!window.confirm(`¿Eliminar la zona "${f.nombre}"?`)) return;
    try { await api.delete(`/fleet/geofences/${f.id}`); toast.success("Zona eliminada"); load(); onChange?.(); }
    catch { toast.error("Error"); }
  };
  const unitName = (id) => (units.find((u) => u.id === id) || {}).nombre || "unidad";

  return (
    <div className="card-surface p-6" data-testid="fleet-geofences-card">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <MapPin className="w-4 h-4 text-violet" />
        <h3 className="font-medium">Geocercas (zonas permitidas)</h3>
        <button onClick={() => setFormOpen(!formOpen)} data-testid="geofence-new-btn"
          className="ml-auto rounded-full border border-navy/15 text-xs px-4 py-2 hover:border-violet hover:text-violet transition-colors inline-flex items-center gap-1.5">
          <Plus className="w-3.5 h-3.5" /> {formOpen ? "Cerrar" : "Nueva zona"}
        </button>
      </div>
      <p className="text-xs text-navy/50 mb-3 max-w-2xl">
        Define zonas circulares permitidas. En cada sincronización con Strix, si una unidad está fuera de todas sus
        zonas recibirás una alerta en la Bitácora y la campana (y saldrá en el reporte semanal).
      </p>

      {formOpen && (
        <div className="rounded-xl border border-violet/25 bg-violet/[0.02] p-4 mb-4 space-y-3" data-testid="geofence-form">
          <div className="grid md:grid-cols-3 gap-2">
            <input className={inputCls} placeholder="Nombre de la zona (ej. CEDIS Norte)" value={form.nombre}
              onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="geofence-name" />
            <div className="flex items-center gap-2">
              <input type="number" min={50} step={50} className={inputCls} value={form.radio_m} data-testid="geofence-radius"
                onChange={(e) => setForm({ ...form, radio_m: e.target.value })} />
              <span className="text-xs text-navy/50 whitespace-nowrap">radio en metros</span>
            </div>
            <div className="flex items-center gap-2">
              <input type="number" min={10} max={200} className={inputCls} value={form.limite_velocidad} data-testid="geofence-speed-limit"
                placeholder="Límite km/h (opcional)" onChange={(e) => setForm({ ...form, limite_velocidad: e.target.value })} />
              <span className="text-xs text-navy/50 whitespace-nowrap" title="Si lo configuras, dentro de esta zona rige este límite de velocidad en lugar del global. Vacío = no aplica.">límite en la zona</span>
            </div>
            <div className="text-xs text-navy/50 self-center" data-testid="geofence-center">
              {form.lat != null ? <>Centro: <b>{form.lat}, {form.lng}</b></> : "Haz clic en el mapa para fijar el centro"}
            </div>
          </div>
          <div ref={miniRef} style={{ height: 300 }} className="rounded-xl border border-navy/10 overflow-hidden" data-testid="geofence-map" />
          {units.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-navy/50">Aplica a:</span>
              <button onClick={() => setForm({ ...form, unit_ids: [] })}
                className={`rounded-full px-3 py-1 border ${form.unit_ids.length === 0 ? "bg-navy text-cream border-navy" : "border-navy/15 text-navy/60"}`}>
                Todas las unidades
              </button>
              {units.filter((u) => u.estado !== "baja").map((u) => (
                <button key={u.id} data-testid={`geofence-unit-${u.id}`}
                  onClick={() => setForm({ ...form, unit_ids: form.unit_ids.includes(u.id) ? form.unit_ids.filter((x) => x !== u.id) : [...form.unit_ids, u.id] })}
                  className={`rounded-full px-3 py-1 border ${form.unit_ids.includes(u.id) ? "bg-violet text-white border-violet" : "border-navy/15 text-navy/60"}`}>
                  {u.nombre}
                </button>
              ))}
            </div>
          )}
          <button onClick={save} disabled={saving} data-testid="geofence-save"
            className="rounded-full bg-navy text-cream text-sm px-5 py-2.5 hover:bg-navy-soft transition-colors inline-flex items-center gap-1.5 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />} Guardar zona
          </button>
        </div>
      )}

      {fences.length === 0 ? (
        !formOpen && <div className="text-sm text-navy/40 border border-dashed border-navy/15 rounded-lg py-5 text-center">Sin zonas definidas. Crea la primera con "Nueva zona".</div>
      ) : (
        <div className="bg-white border border-navy/10 rounded-lg divide-y divide-navy/5">
          {fences.map((f) => (
            <div key={f.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5" data-testid={`geofence-row-${f.id}`}>
              <MapPin className={`w-4 h-4 shrink-0 ${f.activo ? "text-violet" : "text-navy/25"}`} />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{f.nombre}</div>
                <div className="text-[11px] text-navy/40">
                  Radio {f.radio_m >= 1000 ? `${(f.radio_m / 1000).toFixed(1)} km` : `${f.radio_m} m`}{f.limite_velocidad ? <span className="text-violet"> · límite {f.limite_velocidad} km/h en la zona</span> : null} · {f.lat}, {f.lng} · {(f.unit_ids || []).length === 0 ? "todas las unidades" : (f.unit_ids || []).map(unitName).join(", ")}
                </div>
              </div>
              <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={!!f.activo} onChange={() => toggleActivo(f)} className="accent-[#6C5CE7]" data-testid={`geofence-toggle-${f.id}`} />
                {f.activo ? "Activa" : "Pausada"}
              </label>
              <button onClick={() => del(f)} className="text-navy/30 hover:text-red-500 transition-colors" data-testid={`geofence-delete-${f.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WeeklyReportCard({ tz }) {
  const [cfg, setCfg] = useState({ enabled: false, emails: "", last_sent: "" });
  const [sending, setSending] = useState(false);
  const [repDate, setRepDate] = useState("");

  useEffect(() => { api.get("/fleet/weekly-report-config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

  const save = async (next) => {
    try {
      const { data } = await api.post("/fleet/weekly-report-config", { enabled: next.enabled, emails: next.emails });
      setCfg((p) => ({ ...p, ...data }));
      toast.success(data.enabled ? "Reporte diario activado: todos los días 10:00 AM" : "Reporte diario desactivado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      // Guarda primero los correos capturados para que el envío use lo que está en pantalla
      if (cfg.emails?.trim()) await api.post("/fleet/weekly-report-config", { enabled: cfg.enabled, emails: cfg.emails });
      const { data } = await api.post("/fleet/weekly-report/send", null, { params: repDate ? { date: repDate } : {} });
      const fallos = (data.enviados || []).filter((x) => x.status === "error");
      const oks = (data.enviados || []).filter((x) => x.status !== "error");
      if (fallos.length === 0) {
        toast.success(data.detail);
        setCfg((p) => ({ ...p, last_sent: new Date().toISOString() }));
      } else {
        if (oks.length > 0) toast.success(`Enviado a ${oks.map((x) => x.email).join(", ")}`);
        const motivo = fallos[0].detail?.includes("verify a domain") || fallos[0].detail?.includes("not verified")
          ? "Tu cuenta de Resend está en modo prueba: solo puede enviar al correo dueño de la cuenta. Verifica tu dominio en resend.com/domains para enviar a cualquier destinatario."
          : fallos[0].detail || "Error del proveedor de correo";
        toast.error(`No se pudo enviar a ${fallos.map((x) => x.email).join(", ")}. ${motivo}`, { duration: 12000 });
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo enviar"); }
    finally { setSending(false); }
  };

  return (
    <div className="card-surface p-6" data-testid="fleet-weekly-card">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <Mail className="w-4 h-4 text-violet" />
        <h3 className="font-medium">Reporte diario por correo</h3>
        <label className="ml-auto inline-flex items-center gap-2 text-xs cursor-pointer select-none">
          <input type="checkbox" checked={cfg.enabled} data-testid="weekly-toggle"
            onChange={(e) => save({ ...cfg, enabled: e.target.checked })} className="accent-[#6C5CE7] w-4 h-4" />
          {cfg.enabled ? "Activado · todos los días 10:00 AM" : "Desactivado"}
        </label>
      </div>
      <p className="text-xs text-navy/50 mb-3 max-w-2xl">
        Todos los días a las 10:00 AM ({tzLabel(tz)}) te enviamos el informe completo del día anterior:
        ranking de excesos &gt;65 km/h (del más crítico al menos crítico), unidades con movimiento nocturno
        (20:00–06:00), km recorridos por unidad, resumen del día, otras alertas y vencimientos próximos.
        El botón "Enviar ahora" manda un informe parcial con lo que va de hoy.
      </p>
      <div className="flex flex-wrap gap-2">
        <input className={`${inputCls} flex-1 min-w-[260px]`} placeholder="Correos destino (separados por coma)"
          value={cfg.emails} data-testid="weekly-emails"
          onChange={(e) => setCfg({ ...cfg, emails: e.target.value })}
          onBlur={() => save(cfg)} />
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-navy/50 whitespace-nowrap">Día:</span>
          <input type="date" max={new Date().toISOString().slice(0, 10)} value={repDate} data-testid="weekly-report-date"
            onChange={(e) => setRepDate(e.target.value)}
            className="bg-white border border-navy/10 rounded-lg px-2 py-2 text-xs outline-none focus:border-violet" />
          {repDate && (
            <button onClick={() => setRepDate("")} className="text-xs text-navy/40 hover:text-violet" data-testid="weekly-report-date-clear" title="Quitar fecha (envía el parcial de hoy)">✕</button>
          )}
        </div>
        <button onClick={sendNow} disabled={sending} data-testid="weekly-send-now"
          className="rounded-full border border-navy/15 text-sm px-5 py-2.5 hover:border-violet hover:text-violet transition-colors inline-flex items-center gap-1.5 disabled:opacity-50">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} {repDate ? `Enviar informe del ${repDate}` : "Enviar ahora"}
        </button>
      </div>
      {cfg.last_sent && <p className="text-[11px] text-navy/40 mt-2" data-testid="weekly-last-sent">Último envío: {fmtTz(cfg.last_sent, tz)} ({tzLabel(tz)})</p>}
    </div>
  );
}

function WhatsAppCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => { api.get("/fleet/whatsapp-config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return null;

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.post("/fleet/whatsapp-config",
        { enabled: next.enabled, numbers: next.numbers, alert_speed: next.alert_speed, alert_geofence: next.alert_geofence });
      setCfg(data);
      toast.success(data.enabled ? "Alertas por WhatsApp activadas" : "Configuración de WhatsApp guardada");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al guardar"); }
    finally { setSaving(false); }
  };
  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/fleet/whatsapp-test");
      if (data.ok) toast.success("Mensaje de prueba enviado — revisa tu WhatsApp");
      else toast.error(data.resultados?.find((r) => r.error)?.error || "No se pudo enviar la prueba");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo enviar la prueba"); }
    finally { setTesting(false); }
  };

  return (
    <div className="card-surface p-6" data-testid="fleet-whatsapp-card">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <MessageCircle className="w-4 h-4 text-emerald-600" />
        <h3 className="font-medium">Alertas por WhatsApp</h3>
        <label className="ml-auto inline-flex items-center gap-2 text-xs cursor-pointer select-none">
          <input type="checkbox" checked={!!cfg.enabled} data-testid="whatsapp-toggle"
            onChange={(e) => save({ ...cfg, enabled: e.target.checked })} className="accent-[#6C5CE7] w-4 h-4" />
          {cfg.enabled ? "Activadas" : "Desactivadas"}
        </label>
      </div>
      <p className="text-xs text-navy/50 mb-3 max-w-2xl">
        Recibe un WhatsApp inmediato cuando una unidad exceda su límite de velocidad o salga de una geocerca.
        Captura los números en formato internacional (+52 1 55…), separados por coma.
      </p>
      {!cfg.configured && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs px-3 py-2 mb-3" data-testid="whatsapp-not-configured">
          ⚠ El servidor aún no tiene las credenciales de Twilio (TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN y TWILIO_WHATSAPP_FROM).
          Tu configuración queda guardada y los envíos se activarán en cuanto se capturen.
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <input className={`${inputCls} flex-1 min-w-[260px]`} placeholder="+5215512345678, +5215598765432"
          value={cfg.numbers} data-testid="whatsapp-numbers"
          onChange={(e) => setCfg({ ...cfg, numbers: e.target.value })} />
        <button onClick={() => save(cfg)} disabled={saving} data-testid="whatsapp-save"
          className="rounded-full bg-navy text-cream text-sm px-5 py-2.5 hover:bg-navy-soft transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Guardar
        </button>
        <button onClick={sendTest} disabled={testing || !cfg.configured} data-testid="whatsapp-test"
          className="rounded-full border border-navy/15 text-sm px-5 py-2.5 hover:border-violet hover:text-violet transition-colors inline-flex items-center gap-1.5 disabled:opacity-50">
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar prueba
        </button>
      </div>
      <div className="flex flex-wrap gap-4 mt-3 text-xs text-navy/60">
        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
          <input type="checkbox" checked={cfg.alert_speed !== false} data-testid="whatsapp-alert-speed"
            onChange={(e) => save({ ...cfg, alert_speed: e.target.checked })} className="accent-[#6C5CE7]" />
          Exceso de velocidad
        </label>
        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
          <input type="checkbox" checked={cfg.alert_geofence !== false} data-testid="whatsapp-alert-geofence"
            onChange={(e) => save({ ...cfg, alert_geofence: e.target.checked })} className="accent-[#6C5CE7]" />
          Salida de geocerca
        </label>
      </div>
    </div>
  );
}

function DriverRankingCard() {
  const [rk, setRk] = useState(null);
  useEffect(() => {
    api.get("/fleet/driver-ranking", { params: { days: 7, limit: 10 } }).then((r) => setRk(r.data)).catch(() => {});
  }, []);
  if (!rk || rk.total_unidades === 0) return null;

  const lider = rk.lideres[0];
  const medal = (pos) => pos === 1 ? "bg-amber-400 text-white" : pos === 2 ? "bg-slate-300 text-navy" : pos === 3 ? "bg-orange-300 text-white" : "bg-navy/5 text-navy/50";

  return (
    <div className="card-surface p-6" data-testid="fleet-ranking-card">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <Trophy className="w-4 h-4 text-amber-500" />
        <h3 className="font-medium">Ranking semanal de conductores</h3>
        <span className="ml-auto text-[11px] text-navy/40">últimos {rk.dias} días · {rk.total_unidades} unidad(es)</span>
      </div>
      <p className="text-xs text-navy/50 mb-4 max-w-2xl">
        Cada unidad inicia con 100 puntos: −15 por exceso de velocidad, −10 por salida de geocerca y −5 por movimiento
        nocturno (20:00–06:00). Los empates se deciden por km recorridos (odómetro Strix, acumulado por día desde la activación del ranking).
      </p>
      {lider && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 mb-4 flex items-center gap-3" data-testid="ranking-leader">
          <div className="w-10 h-10 rounded-full bg-amber-400 text-white flex items-center justify-center shrink-0">
            <Trophy className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="overline text-amber-700/80">Conductor de la semana</div>
            <div className="text-sm font-medium truncate">
              {lider.conductor || lider.nombre}{lider.conductor ? ` · ${lider.nombre}` : lider.placa && lider.placa !== lider.nombre ? ` · ${lider.placa}` : ""}
            </div>
            <div className="text-[11px] text-navy/50">
              {lider.alertas === 0 ? "Sin alertas en la semana" : `${lider.alertas} alerta(s)`} · {Number(lider.km).toLocaleString()} km · {lider.score} pts
            </div>
          </div>
        </div>
      )}
      <div className="bg-white border border-navy/10 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-navy/[0.03] text-xs text-navy/50">
            <tr>
              {["#", "Conductor / Unidad", "Km", "Excesos", "Fuera de zona", "Mov. nocturno", "Puntos"].map((h) => (
                <th key={h} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>))}
            </tr>
          </thead>
          <tbody className="divide-y divide-navy/5">
            {rk.lideres.map((x) => (
              <tr key={x.unit_id} data-testid={`ranking-row-${x.unit_id}`}>
                <td className="px-3 py-2">
                  <span className={`inline-flex w-6 h-6 rounded-full items-center justify-center text-[11px] font-semibold ${medal(x.pos)}`}>{x.pos}</span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className="font-medium">{x.conductor || x.nombre}</span>
                  {x.conductor ? <span className="text-navy/40 text-xs"> · {x.nombre}</span>
                    : x.placa && x.placa !== x.nombre ? <span className="text-navy/40 text-xs"> · {x.placa}</span> : null}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{Number(x.km).toLocaleString()}</td>
                <td className="px-3 py-2">{x.excesos > 0 ? <span className="text-red-600 font-medium">{x.excesos}</span> : <span className="text-navy/30">0</span>}</td>
                <td className="px-3 py-2">{x.fuera_zona > 0 ? <span className="text-amber-600 font-medium">{x.fuera_zona}</span> : <span className="text-navy/30">0</span>}</td>
                <td className="px-3 py-2">{x.mov_nocturno > 0 ? <span className="text-indigo-600 font-medium">{x.mov_nocturno}</span> : <span className="text-navy/30">0</span>}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-1.5 rounded-full bg-navy/10 overflow-hidden">
                      <div className="h-full bg-gradient-violet rounded-full" style={{ width: `${x.score}%` }} />
                    </div>
                    <span className="text-xs font-semibold">{x.score}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rk.con_actividad === 0 && (
        <p className="text-[11px] text-navy/40 mt-2" data-testid="ranking-empty-hint">
          Aún no hay km ni alertas registradas en el periodo: el ranking cobrará vida con cada sincronización de Strix.
        </p>
      )}
    </div>
  );
}

function SugMiniMap({ lat, lng, label, onMove }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const m = L.map(ref.current, { scrollWheelZoom: false }).setView([lat, lng], 16);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 }).addTo(m);
    const circle = L.circle([lat, lng], { radius: 150, color: "#6C5CE7", weight: 1, fillOpacity: 0.08 }).addTo(m);
    const mk = L.marker([lat, lng], { icon: mapDot("#6C5CE7", true), draggable: true, zIndexOffset: 500 }).addTo(m);
    if (label) mk.bindPopup(label).openPopup();
    mk.on("dragend", () => {
      const p = mk.getLatLng();
      circle.setLatLng(p);
      onMove?.(Number(p.lat.toFixed(6)), Number(p.lng.toFixed(6)));
    });
    setTimeout(() => m.invalidateSize(), 150);
    return () => m.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <div ref={ref} className="w-full h-56 rounded-xl overflow-hidden border border-navy/10" data-testid="roster-sug-minimap" />;
}


function VencimientosCard({ onOpenUnit, refreshKey }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filtro, setFiltro] = useState("atencion");
  useEffect(() => { api.get("/fleet/vencimientos-panel").then((r) => setData(r.data)).catch(() => {}); }, [refreshKey]);
  if (!data) return null;
  const { counts, rows, sin_fecha } = data;
  const visibles = rows.filter((r) => (filtro === "todos" ? true : filtro === "atencion" ? r.estado !== "al_dia" : r.estado === filtro));
  const DOT = { vencido: "bg-red-500", por_vencer: "bg-amber-400", al_dia: "bg-emerald-500" };
  const diasTxt = (r) => {
    if (r.estado === "vencido") return r.extra?.includes("CON DEUDA") ? "CON DEUDA" : `hace ${Math.abs(r.dias)} día(s)`;
    if (r.tipo === "Libre deuda") return r.estado === "por_vencer" ? `${r.dias} días sin renovar` : `hace ${r.dias} días ✓`;
    return r.dias === 0 ? "vence HOY" : `en ${r.dias} día(s)`;
  };
  const chip = (key, label, cls, count) => (
    <button key={key} onClick={() => setFiltro(filtro === key ? "atencion" : key)} data-testid={`venc-chip-${key}`}
      className={`rounded-xl border px-4 py-2.5 text-left transition-all ${filtro === key ? "ring-2 ring-violet/40" : ""} ${cls}`}>
      <div className="text-2xl font-semibold leading-none">{count}</div>
      <div className="text-[11px] mt-1 font-medium">{label}</div>
    </button>
  );
  return (
    <div className="card-surface p-6" data-testid="fleet-vencimientos-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-medium flex items-center gap-2"><FileText className="w-4 h-4 text-violet" /> Panel de vencimientos documentales</h3>
          <p className="text-xs text-navy/40 mt-1">Seguro, verificación, tenencia, licencia y libre deuda de las unidades activas. Toca un color para filtrar; toca una fila para abrir su bitácora.</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/os/sheets")} data-testid="venc-subir-renovaciones"
            className="text-xs rounded-full border border-violet/30 text-violet px-3 py-1.5 hover:bg-violet/5 transition-colors flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" /> Subir renovaciones
          </button>
          <button onClick={() => setFiltro(filtro === "todos" ? "atencion" : "todos")} data-testid="venc-chip-todos"
            className="text-xs text-violet hover:underline">{filtro === "todos" ? "ver solo pendientes" : "ver todos"}</button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4 max-w-md">
        {chip("vencido", "Vencidos / con deuda", "border-red-200 bg-red-50 text-red-700", counts.vencido)}
        {chip("por_vencer", "Por vencer (≤30 días)", "border-amber-200 bg-amber-50 text-amber-700", counts.por_vencer)}
        {chip("al_dia", "Al día", "border-emerald-200 bg-emerald-50 text-emerald-700", counts.al_dia)}
      </div>
      {(sin_fecha?.Seguro > 0 || sin_fecha?.["Libre deuda"] > 0) && (
        <div className="text-[11px] text-navy/40 mt-2" data-testid="venc-sin-fecha">
          Sin registrar: {sin_fecha.Seguro > 0 ? `${sin_fecha.Seguro} unidad(es) sin fecha de seguro` : ""}{sin_fecha.Seguro > 0 && sin_fecha["Libre deuda"] > 0 ? " · " : ""}{sin_fecha["Libre deuda"] > 0 ? `${sin_fecha["Libre deuda"]} sin libre deuda` : ""} — se completan solas al subir documentos al Documents Hub.
        </div>
      )}
      <div className="mt-3 max-h-80 overflow-y-auto divide-y divide-navy/5" data-testid="venc-rows">
        {visibles.length === 0 && <div className="text-xs text-navy/40 py-3">Nada pendiente en este filtro ✓</div>}
        {visibles.map((r, i) => (
          <button key={i} onClick={() => onOpenUnit?.(r.unit_id)} data-testid="venc-row"
            className="w-full text-left py-1.5 px-1 flex items-center gap-2 text-xs hover:bg-violet/5 rounded transition-colors">
            <span className={`w-2 h-2 rounded-full shrink-0 ${DOT[r.estado]}`} />
            <span className="font-medium shrink-0">{r.unidad}</span>
            <span className="text-navy/40 shrink-0">{r.placa}</span>
            <span className="shrink-0">· {r.tipo}</span>
            <span className="text-navy/50 truncate">{r.extra && !r.extra.includes("✓") ? r.extra : ""}</span>
            <span className={`ml-auto shrink-0 font-medium ${r.estado === "vencido" ? "text-red-600" : r.estado === "por_vencer" ? "text-amber-600" : "text-emerald-600"}`}>
              {r.vence} · {diasTxt(r)}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}


function RosterCard({ onSynced }) {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [open, setOpen] = useState(false);
  const [pendOpen, setPendOpen] = useState(false);
  const [sugs, setSugs] = useState({}); // {patente: {loading, data, error}}
  const [mapSug, setMapSug] = useState(null); // {patente, rol, lat, lng, nombre, dir, hora, date}
  const [fix, setFix] = useState(null); // {patente, rol, lat, lng}

  const load = useCallback(() => {
    api.get("/fleet/roster").then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data: r } = await api.post("/fleet/roster/sync");
      toast.success(`Planilla sincronizada: ${r.patentes} unidades activas · ${r.bajas_nuevas} baja(s) nueva(s) · ${r.pendientes_geo} dirección(es) sin geocodificar`);
      load(); onSynced?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo sincronizar la planilla"); }
    finally { setSyncing(false); }
  };

  const saveFix = async () => {
    try {
      await api.put(`/fleet/roster/${fix.patente}/coords`, { rol: fix.rol, lat: Number(fix.lat), lng: Number(fix.lng) });
      toast.success("Coordenadas guardadas");
      setFix(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al guardar"); }
  };

  const sugerir = async (patente) => {
    setSugs((s) => ({ ...s, [patente]: { loading: true } }));
    try {
      const { data: d } = await api.get(`/fleet/roster/${patente}/sugerencia`);
      const vacio = !d.sugerencias || !Object.keys(d.sugerencias).length;
      setSugs((s) => ({ ...s, [patente]: { data: d, error: d.sin_datos ? "sin datos GPS en los últimos 5 días" : (vacio ? "el GPS no permitió sugerir una ubicación (últimos 5 días)" : null) } }));
      if (!vacio) {
        const rol = Object.keys(d.sugerencias)[0];
        const sg = d.sugerencias[rol];
        setMapSug({ patente, rol, lat: sg.lat, lng: sg.lng, hora: sg.hora, date: d.date });
      }
    } catch (e) {
      setSugs((s) => ({ ...s, [patente]: { error: formatApiError(e.response?.data?.detail) || "error al consultar el GPS" } }));
    }
  };

  const usarSug = async (patente, rol, sg) => {
    try {
      await api.put(`/fleet/roster/${patente}/coords`, { rol, lat: sg.lat, lng: sg.lng, fuente: "gps" });
      toast.success(`Ubicación de ${rol} de ${patente} fijada desde el GPS`);
      setMapSug(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al guardar"); }
  };

  const rows = data?.rows || [];
  const pend = [];
  rows.forEach((r) => ["chofer", "ayudante", "tecnico"].forEach((rol) => {
    const p = r[rol] || {};
    if ((p.nombre || p.direccion) && p.lat == null) pend.push({ patente: r.patente, rol, nombre: p.nombre, dir: `${p.direccion} ${p.numero}, ${p.localidad}`.trim() });
  }));
  const pendPorPatente = {};
  pend.forEach((p) => { (pendPorPatente[p.patente] = pendPorPatente[p.patente] || []).push(p); });

  return (
    <div className="card-surface p-6" data-testid="fleet-roster-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-violet" />
          <h3 className="font-medium">Planilla CORRECTIVOS · INFO DIRECCIONES</h3>
        </div>
        <button onClick={syncNow} disabled={syncing} data-testid="roster-sync-now"
          className="rounded-full border border-navy/15 text-sm px-5 py-2 hover:border-violet hover:text-violet transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
          <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> Sincronizar planilla
        </button>
      </div>
      <p className="text-xs text-navy/50 mb-3">
        Strix muestra solo las unidades de esta hoja (se actualiza sola cada hora): las que salen de la planilla se dan de baja conservando su historial, las que entran se activan.
      </p>
      {data?.sync?.at && (
        <div className="text-xs text-navy/60 flex flex-wrap gap-x-4 gap-y-1" data-testid="roster-sync-status">
          <span><b>{data.sync.patentes}</b> patentes en la hoja</span>
          <span>última sync: {fmtTz(data.sync.at, "America/Argentina/Buenos_Aires")}</span>
          {pend.length > 0 ? (
            <span className="text-amber-600">
              {pend.length} dirección(es) sin geocodificar
              <button onClick={() => setPendOpen(!pendOpen)} className="ml-1.5 text-violet hover:underline font-medium" data-testid="roster-pendientes-toggle">
                {pendOpen ? "ocultar" : "completar"}
              </button>
            </span>
          ) : <span className="text-emerald-600">todas las direcciones geocodificadas ✓</span>}
          <button onClick={() => setOpen(!open)} className="text-violet hover:underline" data-testid="roster-toggle-detail">{open ? "ocultar detalle" : "ver detalle"}</button>
        </div>
      )}
      {pendOpen && pend.length > 0 && (
        <div className="mt-3 max-h-80 overflow-y-auto border border-amber-200 bg-amber-50/40 rounded-xl divide-y divide-amber-100" data-testid="roster-pendientes">
          <div className="px-3 py-2 text-[11px] text-navy/50">
            Pulsa <b>Sugerir desde GPS</b> para proponer la ubicación según dónde amaneció la unidad (chofer) o sus primeras paradas de la mañana (ayudante/técnico), y fíjala con un clic en <b>Usar</b>.
          </div>
          {Object.entries(pendPorPatente).map(([patente, items]) => {
            const s = sugs[patente];
            return (
              <div key={patente} className="px-3 py-2 text-xs">
                <div className="flex items-center gap-2 flex-wrap">
                  <b>{patente}</b>
                  <button onClick={() => sugerir(patente)} disabled={s?.loading} data-testid={`roster-sug-btn-${patente}`}
                    className="rounded-full border border-navy/15 px-3 py-1 hover:border-violet hover:text-violet transition-colors disabled:opacity-50 inline-flex items-center gap-1">
                    <Satellite className="w-3 h-3" /> {s?.loading ? "consultando GPS…" : "Sugerir desde GPS"}
                  </button>
                  {s?.error && <span className="text-red-600">{s.error}</span>}
                </div>
                {items.map(({ rol, nombre, dir }) => {
                  const sg = s?.data?.sugerencias?.[rol];
                  return (
                    <div key={rol} className="ml-2 mt-1 flex items-center gap-2 flex-wrap text-amber-800">
                      · {rol}: {nombre} — {dir}
                      {sg ? (
                        <>
                          <span className="text-navy/70">GPS: {sg.lat}, {sg.lng} · {sg.hora} del {s.data.date?.slice(5).split("-").reverse().join("/")} ({sg.desc})</span>
                          <button onClick={() => usarSug(patente, rol, sg)} data-testid={`roster-sug-use-${patente}-${rol}`}
                            className="rounded-full bg-navy text-cream px-3 py-1 hover:bg-violet transition-colors">Usar</button>
                          <button onClick={() => setMapSug({ patente, rol, lat: sg.lat, lng: sg.lng, hora: sg.hora, date: s.data.date })}
                            data-testid={`roster-sug-map-btn-${patente}-${rol}`} className="text-violet hover:underline">ver en mapa</button>
                        </>
                      ) : (s?.data && !s.loading && !s.error ? <span className="text-navy/40">sin sugerencia para este rol</span> : null)}
                      <button className="text-violet hover:underline" onClick={() => setFix({ patente, rol, lat: "", lng: "" })}>manual</button>
                    </div>
                  );
                })}
                {mapSug && mapSug.patente === patente && (
                  <div className="mt-2 ml-2" data-testid="roster-sug-map-panel">
                    <div className="text-[11px] text-navy/50 mb-1">
                      Ubicación sugerida para <b>{mapSug.rol}</b> de {patente} ({mapSug.hora} del {mapSug.date?.slice(5).split("-").reverse().join("/")}) — arrastra el marcador para ajustarla · círculo = radio de presencia 150 m
                    </div>
                    <SugMiniMap key={`${mapSug.patente}-${mapSug.rol}`} lat={mapSug.lat} lng={mapSug.lng}
                      label={`${mapSug.rol} · ${patente}`}
                      onMove={(la, ln) => setMapSug((v) => ({ ...v, lat: la, lng: ln }))} />
                    <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                      <button onClick={() => usarSug(patente, mapSug.rol, { lat: mapSug.lat, lng: mapSug.lng })} data-testid="roster-sug-map-confirm"
                        className="rounded-full bg-navy text-cream px-4 py-1.5 hover:bg-violet transition-colors">Confirmar esta ubicación</button>
                      <span className="text-navy/50">{mapSug.lat}, {mapSug.lng}</span>
                      <button onClick={() => setMapSug(null)} className="text-navy/40 hover:text-navy" data-testid="roster-sug-map-close">cerrar</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {open && (
        <div className="mt-3 max-h-72 overflow-y-auto border border-navy/10 rounded-xl divide-y divide-navy/5" data-testid="roster-detail">
          {rows.map((r) => (
            <div key={r.patente} className="px-3 py-2 text-xs">
              <b>{r.patente}</b> · {r.zona} · {r.sector} — chofer {r.chofer?.nombre || "?"}
              {["chofer", "ayudante", "tecnico"].map((rol) => {
                const p = r[rol] || {};
                if (!p.nombre && !p.direccion) return null;
                const ok = p.lat != null;
                return (
                  <div key={rol} className={`ml-2 ${ok ? "text-navy/50" : "text-amber-700"}`}>
                    · {rol}: {p.nombre} — {p.direccion} {p.numero}, {p.localidad} {ok ? `(${p.fuente})` : "— SIN UBICAR"}
                    {!ok && <button className="ml-2 text-violet hover:underline" onClick={() => setFix({ patente: r.patente, rol, lat: "", lng: "" })}>fijar coords</button>}
                    {ok && <button className="ml-2 text-navy/30 hover:text-violet" onClick={() => setFix({ patente: r.patente, rol, lat: p.lat, lng: p.lng })}>corregir</button>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
      {fix && (
        <div className="mt-3 flex items-end gap-2 flex-wrap text-xs bg-violet/5 rounded-xl p-3" data-testid="roster-fix-coords">
          <span>Coordenadas de <b>{fix.rol}</b> de {fix.patente}:</span>
          <input className={`${inputCls} w-36`} placeholder="lat (-34.7...)" value={fix.lat} onChange={(e) => setFix({ ...fix, lat: e.target.value })} />
          <input className={`${inputCls} w-36`} placeholder="lng (-58.6...)" value={fix.lng} onChange={(e) => setFix({ ...fix, lng: e.target.value })} />
          <button onClick={saveFix} className="rounded-full bg-navy text-cream px-4 py-1.5 hover:bg-violet transition-colors">Guardar</button>
          <button onClick={() => setFix(null)} className="text-navy/40 hover:text-navy">Cancelar</button>
        </div>
      )}
    </div>
  );
}


function JornadaCard({ units, tzName, onVerMapa }) {
  const ayer = new Date(Date.now() - 86400000).toLocaleDateString("en-CA", { timeZone: tzName });
  const [date, setDate] = useState(ayer);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const run = async (refresh) => {
    setLoading(true);
    try {
      const { data } = await api.get("/fleet/roster/analysis", { params: { date, ...(refresh ? { refresh: 1 } : {}) } });
      setRows(data.rows || []);
      if (!data.rows?.length) toast.info("Sin análisis para ese día — pulsa Calcular para generarlo");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al cargar el análisis"); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    api.get("/fleet/roster/analysis", { params: { date } }).then((r) => setRows(r.data.rows || [])).catch(() => {});
  }, [date]);

  const vis = (rows || []).filter((a) => !q || (a.patente || "").toLowerCase().includes(q.toLowerCase()) || (a.chofer || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="card-surface p-6" data-testid="fleet-jornada-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Route className="w-4 h-4 text-violet" />
          <h3 className="font-medium">Análisis de jornada por direcciones</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" max={new Date().toISOString().slice(0, 10)} value={date} data-testid="jornada-date"
            onChange={(e) => setDate(e.target.value)} className="bg-white border border-navy/10 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-violet" />
          <input className={`${inputCls} w-40`} placeholder="Filtrar patente/chofer" value={q} onChange={(e) => setQ(e.target.value)} data-testid="jornada-filter" />
          <button onClick={() => run(true)} disabled={loading} data-testid="jornada-run"
            className="rounded-full border border-navy/15 text-xs px-4 py-1.5 hover:border-violet hover:text-violet transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Calcular día completo
          </button>
        </div>
      </div>
      <p className="text-xs text-navy/50 mb-3">Noche en la dirección del chofer, hora de inicio, recogida y entrega de ayudante/técnico, regresos a domicilio (06–20 h) y salidas de zona. Calcular el día completo puede tardar varios minutos (consulta el GPS de cada unidad).</p>
      {rows === null ? <div className="text-xs text-navy/40">Cargando…</div> : vis.length === 0 ? (
        <div className="text-sm text-navy/40 border border-dashed border-navy/15 rounded-lg py-5 text-center" data-testid="jornada-empty">
          Sin análisis calculado para el {date}. Pulsa "Calcular día completo" (también se genera solo con el reporte diario de las 10:00).
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto border border-navy/10 rounded-xl divide-y divide-navy/5" data-testid="jornada-rows">
          {vis.map((a) => (
            <div key={a.unit_id} className="px-4 py-2.5 text-xs" data-testid={`jornada-row-${a.patente}`}>
              <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                <span>{a.patente} <span className="text-navy/40 font-normal">· zona {a.zona || "?"} · chofer {a.chofer || "?"}</span></span>
                <button onClick={() => onVerMapa?.(a.unit_id, a.date || date)} data-testid={`jornada-mapa-${a.patente}`}
                  className="ml-auto shrink-0 text-xs font-normal text-violet/70 hover:text-violet inline-flex items-center gap-1"
                  title="Ver el recorrido de este día en el mapa">
                  <MapIcon className="w-3.5 h-3.5" /> ver recorrido
                </button>
              </div>
              {a.sin_datos ? <div className="text-navy/40">Sin datos GPS en el día</div> : (
                <ul className="mt-1 space-y-0.5 text-navy/70">
                  <li>{a.noche_en_chofer ? "✅ Durmió en la dirección del chofer" : <span className="text-amber-700">⚠️ NO durmió en la dirección del chofer — amaneció en {a.noche_lugar}</span>}</li>
                  <li>{a.inicio_hora ? <>🕖 Inició a las <b>{a.inicio_hora}</b> desde {a.inicio_desde}{!a.inicio_desde_chofer && <span className="text-amber-700"> (no desde la dirección del chofer)</span>}</> : "Sin movimiento en el día"}</li>
                  {a.recogida_ayudante && <li>👥 Ayudante {a.ayudante}: {a.recogida_ayudante.includes(":") ? `recogido a las ${a.recogida_ayudante}` : a.recogida_ayudante}{a.dejo_ayudante && ` · dejado a las ${a.dejo_ayudante}`}</li>}
                  {a.recogida_tecnico && <li>🔧 Técnico {a.tecnico}: {a.recogida_tecnico.includes(":") ? `recogido a las ${a.recogida_tecnico}` : a.recogida_tecnico}{a.dejo_tecnico && ` · dejado a las ${a.dejo_tecnico}`}</li>}
                  {(a.regresos || []).map((r, i) => <li key={i} className="text-amber-700">🏠 Regresó a la dirección del {r.rol} ({r.quien}) de {r.desde} a {r.hasta}</li>)}
                  {(a.fuera_zona || []).map((t, i) => t.hacia_base
                    ? <li key={`z${i}`} className="text-yellow-600">🟡 Salida de zona hacia/desde la base de {t.desde} a {t.hasta} (autorizada)</li>
                    : <li key={`z${i}`} className="text-sky-600">🔵 FUERA de su zona {a.zona} de {t.desde} a {t.hasta}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function AlertParamsCard({ strix, onSaved }) {
  const [f, setF] = useState({ limite_velocidad: 65, nocturno_inicio: "20:00", nocturno_fin: "06:00", sin_senal_horas: 6 });
  const [aplicar, setAplicar] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (strix) setF({ limite_velocidad: strix.limite_velocidad || 65, nocturno_inicio: strix.nocturno_inicio || "20:00", nocturno_fin: strix.nocturno_fin || "06:00", sin_senal_horas: strix.sin_senal_horas ?? 6 });
  }, [strix?.limite_velocidad, strix?.nocturno_inicio, strix?.nocturno_fin, strix?.sin_senal_horas]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/fleet/alert-settings", { ...f, limite_velocidad: Number(f.limite_velocidad) || 65, sin_senal_horas: Number(f.sin_senal_horas) || 0, aplicar_a_unidades: aplicar });
      toast.success(aplicar ? `Parámetros guardados — rige el límite global de ${data.limite_velocidad} km/h${data.eventos_purgados ? ` · ${data.eventos_purgados} evento(s) por debajo del límite eliminados` : ""}` : "Parámetros de alertas guardados");
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudieron guardar los parámetros"); }
    finally { setSaving(false); }
  };

  return (
    <div className="card-surface p-6" data-testid="fleet-alert-params-card">
      <div className="flex items-center gap-2 mb-1">
        <Gauge className="w-4 h-4 text-violet" />
        <h3 className="font-medium">Parámetros de alertas</h3>
      </div>
      <p className="text-xs text-navy/50 mb-4">
        Límite de velocidad global y ventana de movimiento nocturno (hora local del tenant). Los cambios aplican a los eventos nuevos; usa "Re-escanear 7 días" en Eventos de Alerta para recalcular días pasados.
      </p>
      <div className="flex flex-wrap items-end gap-4">
        <label className="text-xs text-navy/60">
          Límite de velocidad global (km/h)
          <input type="number" min="10" max="200" className={`${inputCls} mt-1 w-40 block`} value={f.limite_velocidad} data-testid="alert-params-limite"
            onChange={(e) => setF({ ...f, limite_velocidad: e.target.value })} />
        </label>
        <label className="text-xs text-navy/60">
          Nocturno desde
          <input type="time" className={`${inputCls} mt-1 w-32 block`} value={f.nocturno_inicio} data-testid="alert-params-noc-inicio"
            onChange={(e) => setF({ ...f, nocturno_inicio: e.target.value })} />
        </label>
        <label className="text-xs text-navy/60">
          Nocturno hasta
          <input type="time" className={`${inputCls} mt-1 w-32 block`} value={f.nocturno_fin} data-testid="alert-params-noc-fin"
            onChange={(e) => setF({ ...f, nocturno_fin: e.target.value })} />
        </label>
        <label className="text-xs text-navy/60" title="Avisa por campana, correo (destinatarios del reporte) y WhatsApp cuando una unidad lleve más de X horas sin transmitir GPS. 0 = desactivado.">
          Sin señal GPS (horas · 0 = off)
          <input type="number" min="0" max="168" className={`${inputCls} mt-1 w-36 block`} value={f.sin_senal_horas} data-testid="alert-params-sin-senal"
            onChange={(e) => setF({ ...f, sin_senal_horas: e.target.value })} />
        </label>
        <button onClick={save} disabled={saving} data-testid="alert-params-save"
          className="rounded-full bg-navy text-cream text-sm px-5 py-2 hover:bg-violet transition-colors disabled:opacity-40">
          {saving ? "Guardando…" : "Guardar parámetros"}
        </button>
      </div>
      <label className="flex items-center gap-2 mt-3 text-xs text-navy/60 cursor-pointer">
        <input type="checkbox" checked={aplicar} onChange={(e) => setAplicar(e.target.checked)} data-testid="alert-params-aplicar" className="accent-violet" />
        Aplicar el límite global a todas las unidades (borra los límites individuales que tengan configurados)
      </label>
    </div>
  );
}


function StrixBlock({ unit, tz, globalLimit = 65 }) {
  const [summary, setSummary] = useState(null);
  const live = unit.strix_live || {};
  useEffect(() => {
    api.get(`/fleet/units/${unit.id}/strix-summary`, { params: { days: 7 } })
      .then((r) => setSummary(r.data)).catch(() => setSummary(null));
  }, [unit.id]);

  return (
    <div data-testid="strix-block">
      <div className="overline text-navy/40 mb-2 flex items-center gap-1.5"><Satellite className="w-3.5 h-3.5 text-violet" /> Strix — datos reales</div>
      {live.lat != null && (
        <div className="text-sm mb-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="strix-live-line">
          <span className={`inline-flex items-center gap-1.5 ${isStale(live) ? "text-navy/40" : (Number(live.speed) >= 7 || live.contact_on) ? "text-emerald-700" : "text-navy/50"}`}>
            <span className={`w-2 h-2 rounded-full ${isStale(live) ? "bg-slate-300" : (Number(live.speed) >= 7 || live.contact_on) ? "bg-emerald-500 animate-pulse" : "bg-navy/20"}`} />
            {isStale(live) ? `Sin señal desde ${fmtTz(live.datetime, tz)}` : `${live.contact_on ? "Encendida" : "Apagada"}${live.speed != null ? ` · ${live.speed} km/h` : ""}`}
          </span>
          <a href={`https://maps.google.com/?q=${live.lat},${live.lng}`} target="_blank" rel="noreferrer" className="text-violet text-xs hover:underline">Ver posición en Google Maps →</a>
          {live.updated_at && <span className="text-[11px] text-navy/40">actualizado {fmtTz(live.updated_at, tz)} ({tzLabel(tz)})</span>}
        </div>
      )}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2" data-testid="strix-summary">
          {[["Km (7 días)", summary.km?.toLocaleString()], ["Vel. máx", `${summary.velocidad_max} km/h`],
            ["En movimiento", `${summary.horas_movimiento} h`], ["Detenida", `${summary.horas_detenido} h`],
            ["Apagada", `${summary.horas_apagado} h`]].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-navy/10 bg-white px-3 py-2">
              <div className="text-sm font-medium">{v}</div>
              <div className="text-[10px] text-navy/40">{l}</div>
            </div>
          ))}
        </div>
      )}
      <TrailExplorer unit={unit} tz={tz} globalLimit={globalLimit} />
    </div>
  );
}

function TrailExplorer({ unit, autoLoad = false, tz, episode = null, globalLimit = 65 }) {
  const [trail, setTrail] = useState(null);
  const [trailDate, setTrailDate] = useState(episode?.date || "");
  const [trailShownDate, setTrailShownDate] = useState("");
  const [loadingTrail, setLoadingTrail] = useState(false);
  const [fences, setFences] = useState([]);
  const [homes, setHomes] = useState([]);
  const baseLim = Number(unit.limite_velocidad) || globalLimit;
  const effLim = (p) => zoneLimit(p, unit.id, fences) || baseLim;

  useEffect(() => {
    api.get("/fleet/geofences").then((r) => setFences((r.data || []).filter((g) => g.activo && g.limite_velocidad))).catch(() => {});
  }, [unit.id]);

  const loadTrail = async (date) => {
    setLoadingTrail(true);
    try {
      const { data } = await api.get(`/fleet/units/${unit.id}/strix-trail`, { params: date ? { date } : { hours: 24 } });
      setTrail(data.puntos || []);
      setHomes(data.domicilios || []);
      setTrailShownDate(date || "");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cargar el recorrido"); }
    finally { setLoadingTrail(false); }
  };

  useEffect(() => { if (autoLoad) loadTrail(episode?.date || ""); }, [unit.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="trail-explorer">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => (trail ? setTrail(null) : loadTrail(""))} disabled={loadingTrail} data-testid="strix-trail-btn"
          className="text-xs text-violet hover:underline inline-flex items-center gap-1 disabled:opacity-50">
          {loadingTrail && !trailDate ? <Loader2 className="w-3 h-3 animate-spin" /> : null} {trail ? "Ocultar recorrido" : "Ver recorrido de las últimas 24 h"}
        </button>
        <span className="text-navy/20">·</span>
        <span className="text-[11px] text-navy/50">o elige un día:</span>
        <input type="date" max={new Date().toISOString().slice(0, 10)} value={trailDate} data-testid="strix-trail-date"
          onChange={(e) => setTrailDate(e.target.value)}
          className="bg-white border border-navy/10 rounded-lg px-2 py-1 text-xs outline-none focus:border-violet" />
        <button onClick={() => trailDate && loadTrail(trailDate)} disabled={loadingTrail || !trailDate} data-testid="strix-trail-date-btn"
          className="rounded-full border border-navy/15 text-xs px-3 py-1 hover:border-violet hover:text-violet transition-colors disabled:opacity-40 inline-flex items-center gap-1">
          {loadingTrail && trailDate ? <Loader2 className="w-3 h-3 animate-spin" /> : <MapIcon className="w-3 h-3" />} Ver viaje del día
        </button>
      </div>
      {trail && (
        <div className="mt-2 space-y-2">
          {episode?.from && (
            <div className="text-[11px] rounded-lg bg-red-50 border border-red-200 text-red-700 px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="trail-episode-chip">
              <ShieldAlert className="w-3.5 h-3.5" /> Tramo del exceso resaltado: {fmtTzTime(episode.from, tz)}–{fmtTzTime(episode.to, tz)} · {episode.velocidad} km/h máx{episode.puntos ? ` · ${episode.puntos} punto(s)` : ""}
            </div>
          )}
          <div className="text-[11px] text-navy/50" data-testid="strix-trail-caption">
            {trailShownDate ? `Recorrido del ${trailShownDate}` : "Recorrido de las últimas 24 h"} — {trail.length} punto(s) GPS
          </div>
          {trail.length > 0 && <TrailMap points={trail} tz={tz} limit={baseLim} fences={fences} unitId={unit.id} highlight={episode} homes={homes} />}
          <div className="max-h-40 overflow-auto border border-navy/10 rounded-lg bg-white divide-y divide-navy/5" data-testid="strix-trail-list">
          {trail.length === 0 ? <p className="text-xs text-navy/40 p-3">{trailShownDate ? `Sin puntos de recorrido el ${trailShownDate}.${trailShownDate === new Date().toISOString().slice(0, 10) ? " La unidad no ha transmitido recorrido hoy — si el GPS dejó de reportar verás \"sin señal desde…\" en la lista de unidades." : ""}` : "Sin puntos de recorrido en las últimas 24 horas."}</p> :
            trail.map((p, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-1.5 text-xs">
                <span className="text-navy/40 w-32 shrink-0">{fmtTz(p.datetime, tz)}</span>
                <span className={`w-16 ${(p.speed || 0) > effLim(p) ? "text-red-600 font-semibold" : ""}`}>{p.speed != null ? `${p.speed} km/h` : "—"}</span>
                <span className="text-navy/40">{p.km ? `${p.km.toLocaleString()} km` : ""}</span>
                {p.lat != null && <a href={`https://maps.google.com/?q=${p.lat},${p.lng}`} target="_blank" rel="noreferrer" className="text-violet hover:underline ml-auto">mapa</a>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TrailMap({ points, tz, limit = 65, highlight = null, fences = [], unitId = "", homes = [] }) {
  const ref = useRef(null);
  const markerRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const effLim = (p) => zoneLimit(p, unitId, fences) || limit;

  useEffect(() => {
    setIdx(0); setPlaying(false);
    if (!ref.current || points.length === 0) return;
    const latlngs = points.map((p) => [p.lat, p.lng]);
    const m = L.map(ref.current, { scrollWheelZoom: true });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap", maxZoom: 19 }).addTo(m);
    L.polyline(latlngs, { color: "#6C5CE7", weight: 3, opacity: 0.75 }).addTo(m);
    // tramos fuera de zona: celeste; hacia/desde base: amarillo
    const drawRuns = (flag, color) => {
      let run = [];
      points.forEach((p, i) => {
        if (p[flag] && p.lat != null) {
          run.push([p.lat, p.lng]);
        } else if (run.length) {
          if (run.length >= 2) L.polyline(run, { color, weight: 6, opacity: 0.85 }).addTo(m);
          run = [];
        }
        if (i === points.length - 1 && run.length >= 2) L.polyline(run, { color, weight: 6, opacity: 0.85 }).addTo(m);
      });
    };
    drawRuns("fz", "#0EA5E9");
    drawRuns("fzb", "#F59E0B");
    let hl = [];
    if (highlight?.from && highlight?.to) {
      const f = new Date(highlight.from).getTime() - 30000, t = new Date(highlight.to).getTime() + 30000;
      hl = points.filter((p) => {
        const ms = new Date(p.datetime).getTime();
        return ms >= f && ms <= t && p.lat != null;
      });
      if (hl.length >= 2) L.polyline(hl.map((p) => [p.lat, p.lng]), { color: "#DC2626", weight: 6, opacity: 0.85 }).addTo(m);
    }
    points.forEach((p) => {
      const le = effLim(p);
      if ((p.speed || 0) > le && p.lat != null) {
        L.circleMarker([p.lat, p.lng], { radius: 5.5, color: "#fff", weight: 1.5, fillColor: "#DC2626", fillOpacity: 0.95 })
          .bindPopup(`<b style="color:#DC2626">Exceso: ${p.speed} km/h</b> (límite ${le}${le !== limit ? " de zona" : ""} km/h)<br/>${fmtTz(p.datetime, tz)}`)
          .addTo(m);
      }
    });
    L.marker(latlngs[0], { icon: mapDot("#10B981") }).bindPopup("Inicio").addTo(m);
    L.marker(latlngs[latlngs.length - 1], { icon: mapDot("#F43F5E") }).bindPopup("Fin").addTo(m);
    homes.forEach((h) => {
      const rolBase = h.rol.split(" ")[0];
      const color = HOME_COLORS[rolBase] || "#0E1A2B";
      const etiqueta = h.rol.split(" + ").map((r) => HOME_LABELS[r] || r).join(" + ");
      L.circle([h.lat, h.lng], { radius: 150, color, weight: 1, dashArray: "4 4", fillOpacity: 0.06 }).addTo(m);
      L.marker([h.lat, h.lng], { icon: homePin(color), zIndexOffset: 400 })
        .bindPopup(`<b style="color:${color}">${etiqueta}</b>${h.nombre && h.rol !== "base" ? `: ${h.nombre}` : ""}<br/>${h.direccion}${h.fuente && h.fuente !== "georef" ? `<br/><span style="opacity:.6">ubicación por ${h.fuente}</span>` : ""}`)
        .addTo(m);
    });
    markerRef.current = L.marker(latlngs[0], { icon: mapDot("#0E1A2B", true), zIndexOffset: 500 }).addTo(m);
    if (hl.length >= 2) m.fitBounds(hl.map((p) => [p.lat, p.lng]), { padding: [45, 45] });
    else if (hl.length === 1) m.setView([hl[0].lat, hl[0].lng], 15);
    else m.fitBounds(latlngs, { padding: [25, 25] });
    if (hl.length > 0) setIdx(Math.max(points.indexOf(hl[0]), 0));
    setTimeout(() => m.invalidateSize(), 200);
    return () => { m.remove(); markerRef.current = null; };
  }, [points, highlight, fences, homes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const p = points[idx];
    if (markerRef.current && p) markerRef.current.setLatLng([p.lat, p.lng]);
  }, [idx, points]);

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setIdx((i) => {
      if (i >= points.length - 1) { setPlaying(false); return i; }
      return i + 1;
    }), 140);
    return () => clearInterval(id);
  }, [playing, points.length]);

  const cur = points[idx] || {};
  const excesos = points.filter((p) => (p.speed || 0) > effLim(p)).length;
  const zonasTxt = fences.length ? " o el límite de cada zona" : "";
  return (
    <div data-testid="strix-trail-map-wrap">
      <div ref={ref} style={{ height: 260 }} className="rounded-xl border border-navy/10 overflow-hidden" data-testid="strix-trail-map" />
      <div className="text-[11px] mt-1.5" data-testid="strix-trail-exceso-legend">
        {excesos > 0
          ? <span className="text-red-600 font-medium">● {excesos} punto(s) en rojo = exceso de velocidad (&gt; {limit} km/h{zonasTxt})</span>
          : <span className="text-navy/40">Sin excesos de velocidad (&gt; {limit} km/h{zonasTxt}) en este recorrido ✓</span>}
      </div>
      {(points.some((p) => p.fz) || points.some((p) => p.fzb)) && (
        <div className="text-[11px] mt-0.5 flex flex-wrap gap-3" data-testid="strix-trail-zona-legend">
          {points.some((p) => p.fz) && <span className="text-sky-600 font-medium">▬ tramo fuera de su zona asignada</span>}
          {points.some((p) => p.fzb) && <span className="text-yellow-600 font-medium">▬ fuera de zona hacia/desde la base (autorizado)</span>}
        </div>
      )}
      {homes.length > 0 && (
        <div className="text-[11px] mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 items-center" data-testid="strix-trail-homes-legend">
          <span className="text-navy/40">Domicilios (radio 150 m):</span>
          {homes.map((h) => {
            const rolBase = h.rol.split(" ")[0];
            const etiqueta = h.rol.split(" + ").map((r) => HOME_LABELS[r] || r).join(" + ");
            return (
              <span key={h.rol} className="inline-flex items-center gap-1 font-medium" style={{ color: HOME_COLORS[rolBase] || "#0E1A2B" }}>
                <Home className="w-3 h-3" /> {etiqueta}{h.nombre && h.rol !== "base" ? ` (${h.nombre})` : ""}
              </span>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-2 mt-2">
        <button onClick={() => { if (!playing && idx >= points.length - 1) setIdx(0); setPlaying(!playing); }} data-testid="strix-trail-play"
          className="rounded-full bg-navy text-cream p-2 hover:bg-navy-soft transition-colors shrink-0" title={playing ? "Pausar" : "Reproducir viaje"}>
          {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
        </button>
        <input type="range" min={0} max={Math.max(points.length - 1, 0)} value={idx} data-testid="strix-trail-slider"
          onChange={(e) => { setPlaying(false); setIdx(Number(e.target.value)); }} className="flex-1 accent-[#6C5CE7]" />
        <span className="text-[11px] text-navy/50 w-40 text-right shrink-0" data-testid="strix-trail-cursor">
          {cur.datetime ? fmtTzTime(cur.datetime, tz) : "--:--"} · {cur.speed != null ? `${cur.speed} km/h` : "—"}
        </span>
      </div>
    </div>
  );
}

function UnitDocs({ unitId }) {
  const [docs, setDocs] = useState([]);
  const [tipo, setTipo] = useState("poliza");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/fleet/units/${unitId}/docs`).then((r) => setDocs(r.data)).catch(() => {});
  }, [unitId]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Selecciona el PDF"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("tipo", tipo);
      fd.append("file", file);
      await api.post(`/fleet/units/${unitId}/docs/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento adjuntado");
      setFile(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error al subir"); }
    finally { setBusy(false); }
  };
  const del = async (d) => {
    if (!window.confirm(`¿Eliminar "${d.tipo_label}" (${d.filename})?`)) return;
    try { await api.delete(`/fleet/units/${unitId}/docs/${d.id}`); load(); } catch { toast.error("Error"); }
  };

  return (
    <div data-testid="unit-docs">
      {docs.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 text-sm" data-testid={`unit-doc-${d.id}`}>
              <FileText className="w-4 h-4 text-violet shrink-0" />
              <span className="font-medium">{d.tipo_label}</span>
              <span className="text-navy/40 text-xs truncate">{d.filename} · {(d.size / 1048576).toFixed(1)} MB</span>
              <a href={d.url} target="_blank" rel="noreferrer" className="text-navy/40 hover:text-violet ml-auto" title="Ver" data-testid={`unit-doc-view-${d.id}`}>
                <ExternalLink className="w-4 h-4" /></a>
              <button onClick={() => del(d)} className="text-navy/30 hover:text-destructive" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <select className={`${inputCls} !w-auto`} value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="unit-doc-tipo">
          {DOC_TIPOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label className="flex-1 min-w-[180px] cursor-pointer border border-dashed border-navy/20 rounded-lg px-3 py-2.5 text-sm text-navy/50 hover:border-violet transition-colors truncate">
          <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="unit-doc-file"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <Paperclip className="w-3.5 h-3.5 inline mr-1.5" />{file ? file.name : "Elegir PDF (máx. 25 MB)"}
        </label>
        <button onClick={upload} disabled={busy} data-testid="unit-doc-submit"
          className="rounded-full bg-navy text-cream text-sm px-4 py-2.5 hover:bg-navy-soft transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Adjuntar
        </button>
      </div>
      <p className="text-[11px] text-navy/40 mt-2">Los documentos son privados: solo usuarios de tu empresa con sesión activa pueden abrirlos (enlace firmado de 1 hora).</p>
    </div>
  );
}
